import { useState, useEffect } from 'react';
import { api } from '../api';
import ValueBadge from '../components/ValueBadge';
import VibeTags from '../components/VibeTags';
import septemberContext from '../septemberContext';
import { stepCopy } from '../copy';
import DayByDay from '../components/DayByDay';
import PackingList from '../components/PackingList';
import MockupCompare from '../components/MockupCompare';
import bookingLinks from '../bookingLinks';
import AiTip from '../components/AiTip';
import DestImage from '../components/DestImage';
import Confetti from '../components/Confetti';
import AnimatedCounter from '../components/AnimatedCounter';
import TripCalendar from '../components/TripCalendar';
import { useTripBuilder } from '../TripBuilderContext';
import LiveAvailability from '../components/LiveAvailability';
import LiveHotels from '../components/LiveHotels';
import TransferBonusAlert from '../components/TransferBonusAlert';
import DayByDayBuilder from '../components/DayByDayBuilder';
import MapView from '../components/MapView';
import MultiCityPlanner from '../components/MultiCityPlanner';
import RouteRibbon from '../components/RouteRibbon';
import StopHotelPicker from '../components/StopHotelPicker';
import StopActivitiesPicker from '../components/StopActivitiesPicker';
import StopRestaurantsPicker from '../components/StopRestaurantsPicker';
import HotelInsightCard from '../components/HotelInsightCard';
import RestaurantBookingPanel from '../components/RestaurantBookingPanel';
import DailyPlanSuggester from '../components/DailyPlanSuggester';
import TripGuidanceCard from '../components/TripGuidanceCard';
import AutoPlanner from '../components/AutoPlanner';
import LivePointsTracker from '../components/LivePointsTracker';
import { getCityDuration } from '../tripGuidance';
import { getTransportInfo } from '../transport';
import ShareTrip from '../components/ShareTrip';
import CalendarExport from '../components/CalendarExport';

const steps = [
  { id: 'dates', label: 'When', num: 1 },
  { id: 'destination', label: 'Where', num: 2 },
  { id: 'flights', label: 'Flights', num: 3 },
  { id: 'hotel', label: 'Hotel', num: 4 },
  { id: 'activities', label: 'Do', num: 5 },
  { id: 'food', label: 'Eat', num: 6 },
  { id: 'review', label: 'Review', num: 7 },
];

export default function PlanFlow({ trip }) {
  const tb = useTripBuilder();
  const [step, setStep] = useState('dates');
  const [destinations, setDestinations] = useState([]);
  const [destDetail, setDestDetail] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState(null);
  const [liveHasData, setLiveHasData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confettiKey, setConfettiKey] = useState(0);
  const [showSaveMockup, setShowSaveMockup] = useState(false);
  const [autoPlannerOpen, setAutoPlannerOpen] = useState(false);
  const [mockupName, setMockupName] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [detailType, setDetailType] = useState(null);

  // Aliases from context
  const pickedDest = tb.destination;
  const pickedFlight = tb.flight;
  const pickedHotel = tb.hotel;
  const pickedActivities = tb.activities;
  const pickedRestaurants = tb.restaurants;

  useEffect(() => {
    api.getDestinations().then(d => { setDestinations(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Load destination detail when picked
  useEffect(() => {
    if (pickedDest) {
      Promise.all([
        api.getDestination(pickedDest.id),
        api.getActivities(pickedDest.id),
        api.getRestaurants(pickedDest.id),
      ]).then(([d, a, r]) => { setDestDetail(d); setAllActivities(a); setAllRestaurants(r); });
    }
  }, [pickedDest]);

  const daysUntil = Math.ceil((new Date('2026-09-01') - new Date()) / (1000 * 60 * 60 * 24));

  const goNext = () => {
    const idx = steps.findIndex(s => s.id === step);
    if (idx < steps.length - 1) {
      setStep(steps[idx + 1].id);
    }
  };
  const goBack = () => {
    const idx = steps.findIndex(s => s.id === step);
    if (idx > 0) setStep(steps[idx - 1].id);
  };
  const canProgress = () => {
    if (step === 'dates') return !!(tb.startDate && tb.endDate);
    if (step === 'destination') return !!pickedDest;
    return true;
  };

  const handleSaveMockup = () => {
    const name = mockupName.trim() || `${pickedDest?.city || 'Trip'} Plan`;
    tb.saveMockup(name);
    setShowSaveMockup(false);
    setMockupName('');
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2000);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto pb-20">
        <div className="text-center mb-8 space-y-3">
          <div className="animate-shimmer h-4 w-32 mx-auto rounded" />
          <div className="animate-shimmer h-10 w-full max-w-sm mx-auto rounded-lg" />
          <div className="animate-shimmer h-4 w-64 mx-auto rounded" />
        </div>
        <div className="flex items-center justify-center gap-1 mb-8">
          {[0,1,2,3,4,5].map(i => (
            <div key={i} className="flex items-center">
              <div className="animate-shimmer w-20 h-8 rounded-full" style={{ animationDelay: `${i * 100}ms` }} />
              {i < 5 && <div className="w-6 h-0.5 mx-1 bg-brand-ink/10" />}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0,1,2,3].map(i => (
            <div key={i} className="rounded-2xl border border-brand-ink/10 overflow-hidden" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="animate-shimmer h-36" />
              <div className="p-4 space-y-2">
                <div className="animate-shimmer h-4 w-3/4 rounded" />
                <div className="animate-shimmer h-3 w-1/2 rounded" />
                <div className="animate-shimmer h-8 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <Confetti trigger={confettiKey} />

      {/* Pilot — AI auto-planner wizard */}
      {autoPlannerOpen && pickedDest && (
        <AutoPlanner
          destination={pickedDest}
          totalNights={Math.max(1, Math.round((new Date(tb.endDate) - new Date(tb.startDate)) / 86400000))}
          travelers={tb.travelers}
          cabinClass={tb.userConfig.cabinClass}
          originAirports={tb.userConfig.origin ? [tb.userConfig.origin] : ['JFK']}
          balances={{ amex_mr: tb.amexTotal, chase_ur: tb.chaseTotal }}
          startDate={tb.startDate}
          endDate={tb.endDate}
          onClose={() => setAutoPlannerOpen(false)}
          onApply={(plan) => {
            // Wrap the entire dispatch in try/catch + force navigation
            // to /my-trip on success. The user reported 'Apply this plan
            // not working' — most likely the dispatch was throwing on a
            // null pick (when Pilot's plan was missing some fields) and
            // silently swallowing the failure.
            try {
              if (plan.per_stop_nights && plan.per_stop_nights.length > 1 && tb.additionalStops.length > 0) {
                const secondStopNights = plan.per_stop_nights[1]?.nights;
                if (secondStopNights) tb.updateStop(0, { nights: secondStopNights });
              }
            // Hotels — apply per stop by matching city name
            const stopCities = [pickedDest.city.split(' + ')[0].trim(), ...tb.additionalStops.map(s => s.destination.city)];
            (plan.hotels || []).forEach((h) => {
              if (!h.hotel) return;
              const stopIdx = stopCities.findIndex(c => c.toLowerCase() === (h.city || '').toLowerCase());
              if (stopIdx === 0) tb.pickHotel(h.hotel);
              else if (stopIdx > 0 && tb.additionalStops[stopIdx - 1]) tb.setStopHotel(stopIdx - 1, h.hotel);
            });

            // Activities + restaurants — split per stop using the city field
            // Pilot returned. For multi-city trips, Porto activities go to
            // the Porto stop, Lisbon activities to Lisbon, etc.
            const activitiesByStop = {};
            const restaurantsByStop = {};
            stopCities.forEach(c => { activitiesByStop[c] = []; restaurantsByStop[c] = []; });
            (plan.activities || []).forEach(a => {
              if (!a.activity) return;
              const match = stopCities.find(c => c.toLowerCase() === (a.city || '').toLowerCase()) || stopCities[0];
              activitiesByStop[match].push(a.activity);
            });
            (plan.restaurants || []).forEach(r => {
              if (!r.restaurant) return;
              const match = stopCities.find(c => c.toLowerCase() === (r.city || '').toLowerCase()) || stopCities[0];
              restaurantsByStop[match].push(r.restaurant);
            });
            // Apply primary stop via legacy actions; additional stops via
            // setStopActivities / setStopRestaurants if available.
            if (activitiesByStop[stopCities[0]].length) tb.pickActivities(activitiesByStop[stopCities[0]]);
            if (restaurantsByStop[stopCities[0]].length) tb.pickRestaurants(restaurantsByStop[stopCities[0]]);
            stopCities.slice(1).forEach((city, i) => {
              if (tb.setStopActivities && activitiesByStop[city].length) tb.setStopActivities(i, activitiesByStop[city]);
              if (tb.setStopRestaurants && restaurantsByStop[city].length) tb.setStopRestaurants(i, restaurantsByStop[city]);
            });

            // Per-leg flight picks — Pilot returned live seats.aero
            // candidates per leg. Build a synth result that matches what
            // LiveAvailability would have produced and dispatch via
            // pickFlightForLeg.
            (plan.flight_picks || []).forEach((fp) => {
              if (!fp.picked || fp.leg_index == null) return;
              const p = fp.picked;
              // Reconstruct a seats.aero-shaped result so the existing
              // buildSynthFlight helper inside pickFlightForLeg works.
              const cabinKeyMap = { economy: 'Y', premium: 'W', business: 'J', first: 'F' };
              const _cabin = tb.userConfig.cabinClass || 'business';
              const cabinKey = cabinKeyMap[_cabin] || 'J';
              const synthResult = {
                ID: p.id,
                Source: p.source,
                OriginAirport: p.route.split(' → ')[0],
                DestinationAirport: p.route.split(' → ')[1],
                Date: p.date,
                [`${cabinKey}MileageCost`]: p.miles_per_person,
                [`${cabinKey}TotalTaxes`]: (p.taxes_total || 0) * 100,
                [`${cabinKey}Airlines`]: p.airlines,
                [`${cabinKey}RemainingSeats`]: p.available_seats,
                [`${cabinKey}DirectMileageCostRaw`]: p.direct ? p.miles_per_person : null,
                [`${cabinKey}Available`]: true,
              };
              tb.pickFlightForLeg(fp.leg_index, synthResult, _cabin);
            });
            } catch (err) {
              console.error('Apply plan dispatch failed (continuing):', err);
              // Don't block the user — they still get whatever was applied
              // before the error + the AutoPlanner closes via onClose. The
              // plan persists in localStorage so they can keep working.
            }
            // ALWAYS surface a confirmation so user knows it worked. Also
            // navigate to My Trip so they see the applied plan immediately.
            try {
              alert('✓ Plan applied! Heading to your trip dashboard…');
              setTimeout(() => { window.location.href = '/my-trip'; }, 100);
            } catch {}
          }}
        />
      )}

      {/* Hero Header */}
      <div className="text-center mb-8">
        <div className="text-brand-ink/55 text-xs font-bold uppercase tracking-[0.3em] mb-2">TRIP PLANNER</div>
        <h1 className="text-3xl sm:text-4xl font-black">
          {pickedDest
            ? (tb.userConfig.travelerNames ? `${tb.userConfig.travelerNames} → ${pickedDest.city}` : `Your trip to ${pickedDest.city}`)
            : 'Plan your next trip'}
        </h1>
        <div className="text-brand-ink/70 mt-2">
          {tb.startDate && tb.endDate ? (
            <>
              {new Date(tb.startDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {' - '}
              {new Date(tb.endDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {' '}
              &middot; {tb.nights} nights
            </>
          ) : 'Pick your dates'}
          {' '}&middot; {daysUntil > 0 ? `${daysUntil} days away` : ''} &middot; {cabinLabel(tb.userConfig.cabinClass)} &middot; {tb.userConfig.travelers} traveler{tb.userConfig.travelers === 1 ? '' : 's'}
        </div>
      </div>

      {/* Live points spend tracker — sticky widget that updates as user
          picks/swaps flights, hotels, activities, restaurants. Stays
          visible across every step of the manual flow. */}
      <div className="sticky top-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 pt-2 bg-navy/95 backdrop-blur">
        <LivePointsTracker />
      </div>

      {/* Plan with your Pilot — hero CTA for end-to-end AI planning */}
      <button
        onClick={() => {
          if (!pickedDest) {
            // Scroll to destination picker if not picked yet
            window.scrollTo({ top: window.innerHeight * 0.6, behavior: 'smooth' });
            return;
          }
          setAutoPlannerOpen(true);
        }}
        className="w-full mb-6 bg-gradient-to-r from-teal via-teal to-gold border-2 border-teal/50 hover:border-teal rounded-2xl p-4 sm:p-5 text-left text-navy shadow-lg hover:shadow-xl transition group"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-navy flex items-center justify-center text-teal font-black text-2xl shrink-0">P</div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">✨ NEW · AI Concierge</div>
            <div className="font-black text-xl mt-0.5">Plan with your Pilot</div>
            <div className="text-xs mt-1 opacity-90 leading-snug">
              {pickedDest
                ? `Auto-plan your ${pickedDest.city} trip — hotels, flights, activities, dinners with reasoning for every pick. Then chat with Pilot to refine it.`
                : 'Pick a destination below, then let Pilot plan the entire trip end-to-end. Chat-driven, points-aware, vibe-tuned.'}
            </div>
          </div>
          <div className="text-2xl font-black opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition">→</div>
        </div>
      </button>

      {/* Progress Tracker */}
      <div className="flex items-center justify-center gap-1 mb-8 overflow-x-auto px-4">
        {steps.map((s, i) => {
          const isCurrent = s.id === step;
          const isPast = steps.findIndex(x => x.id === step) > i;
          const isComplete = (s.id === 'destination' && pickedDest) ||
            (s.id === 'dates' && tb.startDate && tb.endDate) ||
            (s.id === 'flights' && pickedFlight) ||
            (s.id === 'hotel' && pickedHotel) ||
            (s.id === 'activities' && pickedActivities.length > 0) ||
            (s.id === 'food' && pickedRestaurants.length > 0) ||
            (s.id === 'review');
          return (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => setStep(s.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all ${
                  isCurrent ? 'bg-teal text-navy shadow-lg shadow-teal/20 scale-105' :
                  isComplete && isPast ? 'bg-teal/20 text-teal' :
                  'bg-brand-ink/5 text-brand-ink/55 hover:text-brand-ink/70 hover:bg-brand-ink/10'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  isCurrent ? 'bg-navy/20' : isComplete && isPast ? 'bg-teal/30' : 'bg-brand-ink/10'
                }`}>
                  {isComplete && isPast ? '✓' : s.num}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < steps.length - 1 && <div className={`w-6 h-0.5 mx-1 ${isPast ? 'bg-teal/40' : 'bg-brand-ink/10'}`} />}
            </div>
          );
        })}
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div key={step} className="animate-step-enter">

          {/* Route ribbon — shows the shape of the trip on every step after dates */}
          {step !== 'dates' && pickedDest && (
            <div className="mb-4">
              <RouteRibbon />
            </div>
          )}

          {/* ═══ STEP 1: DESTINATION ═══ */}
          {step === 'destination' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold">{stepCopy.destination.heading}</h2>
                <p className="text-brand-ink/70 mt-1">{stepCopy.destination.sub}</p>
              </div>
              <AiTip
                context={`I have ${tb.amexTotal.toLocaleString()} Amex MR + ${tb.chaseTotal.toLocaleString()} Chase UR. Looking at ${tb.userConfig.cabinClass} class for ${tb.userConfig.travelers} traveler${tb.userConfig.travelers === 1 ? '' : 's'} from ${tb.userConfig.origin}.`}
                question="Which destination gives the best combo of value + experience?"
              />
              <DestinationFilterBar tb={tb} destinations={destinations} />

              {/* Custom destination card — first option in the grid so it's
                  unmissable. Lets user plan a trip to ANY city not in the
                  catalog. Pilot AI sources flights + hotels + activities +
                  events via web search + live seats.aero. */}
              <CustomPrimaryDestinationCard onPick={(dest) => { tb.pickDestination(dest); setConfettiKey(k => k + 1); }} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filterDestinationsByMonth(destinations, tb.startDate).map((dest, i) => {
                  const rec = dest.best_redemption?.[0];
                  const selected = pickedDest?.id === dest.id;
                  const ctx = septemberContext[dest.city];
                  return (
                    <div
                      key={dest.id}
                      className={`animate-card-in text-left rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                        selected ? 'border-teal ring-2 ring-teal/40 shadow-lg shadow-teal/10 animate-selected-ring' : 'border-brand-ink/10 hover:border-brand-ink/20'
                      }`}
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="h-36 relative overflow-hidden group cursor-pointer" onClick={() => { setDetailModal(dest); setDetailType('destination'); }}>
                        <DestImage city={dest.city} imgClassName="group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                        {selected && (
                          <div className="absolute top-3 right-3 bg-brand-blue text-brand-cream text-xs font-extrabold px-3 py-1 rounded-full shadow">SELECTED</div>
                        )}
                        {rec?.is_nonstop && (
                          <div className="absolute top-3 left-3 bg-brand-cream/30 backdrop-blur-sm text-brand-cream text-[10px] font-bold px-2 py-0.5 rounded-full">NONSTOP</div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <div className="text-xl font-extrabold text-brand-cream drop-shadow-lg">{dest.city}</div>
                          <div className="text-sm text-brand-cream/90 drop-shadow">{dest.country}</div>
                        </div>
                      </div>
                      <div className="bg-navy-light p-4 space-y-2">
                        <VibeTags tags={dest.vibe_tags} size="xs" />
                        <p className="text-xs text-brand-ink/70 line-clamp-2">{dest.description}</p>
                        {ctx && (
                          <div className="flex items-center gap-2 text-[10px]">
                            <span className={`px-1.5 py-0.5 rounded-full font-bold ${ctx.crowdLevel === 'Very Low' || ctx.crowdLevel === 'Low' ? 'bg-brand-blue/15 text-brand-blue' : 'bg-gold/15 text-gold'}`}>{ctx.crowdLevel} crowds</span>
                            <span className="text-brand-ink/60 truncate">{ctx.weatherVibe} &middot; {ctx.temp}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <span className="font-bold text-brand-ink">{tb.scaleForTravelers(rec?.total_points_two_travelers).toLocaleString()}</span>
                            <span className="text-brand-ink/60 text-xs"> pts via {rec?.airline_program}</span>
                          </div>
                          {rec && <ValueBadge cpp={parseFloat(rec.cents_per_point)} size="sm" />}
                        </div>
                        {rec?.flight_duration_outbound && (
                          <div className="flex items-center gap-2 text-xs">
                            <FlightTimeBadge duration={rec.flight_duration_outbound} />
                            <span className="text-brand-ink/60">{rec.is_nonstop ? 'Nonstop' : `${rec.num_stops} stop`}</span>
                          </div>
                        )}
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => { tb.pickDestination(dest); setConfettiKey(k => k + 1); }}
                            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition ${selected ? 'bg-teal/20 text-teal' : 'bg-teal text-navy hover:bg-teal-dark'}`}
                          >
                            {selected ? 'Selected' : 'Pick This'}
                          </button>
                          <button
                            onClick={() => { setDetailModal(dest); setDetailType('destination'); }}
                            className="px-3 py-2.5 bg-brand-ink/5 text-brand-ink/70 rounded-lg text-sm font-medium hover:bg-brand-ink/10 hover:text-brand-ink/85 transition"
                          >
                            Info
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* September intelligence preview */}
              {pickedDest && septemberContext[pickedDest.city] && (
                <div className="animate-fade-in bg-gradient-to-r from-teal/10 to-blue-900/10 border border-teal/20 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-teal font-bold">{septemberContext[pickedDest.city].weatherVibe} &middot; {septemberContext[pickedDest.city].temp}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      septemberContext[pickedDest.city].crowdLevel === 'Very Low' || septemberContext[pickedDest.city].crowdLevel === 'Low' ? 'bg-teal/15 text-teal' : 'bg-gold/15 text-gold'
                    }`}>{septemberContext[pickedDest.city].crowdLevel} crowds</span>
                  </div>
                  <div className="text-xs text-brand-ink/70 mt-1">{septemberContext[pickedDest.city].bestFor}</div>
                </div>
              )}

              {/* Bundled-destination prompt — show when user picked something
                  like "Lisbon + Porto" but is still in round-trip mode. One
                  click auto-splits into two stops with a suggested nights
                  ratio and unlocks the per-city hotel/activity flow. */}
              {pickedDest && !tb.isMultiCity && pickedDest.city?.includes(' + ') && (
                <div className="bg-gold/10 border border-gold/30 rounded-xl p-3.5 sm:p-4 space-y-2">
                  <div className="font-bold text-sm text-brand-ink">
                    {pickedDest.city} is two cities
                  </div>
                  <div className="text-xs text-brand-ink/75">
                    Plan separately: one hotel in {pickedDest.city.split(' + ')[0]}, one in {pickedDest.city.split(' + ')[1]}, with transport between them. We'll suggest a nights split and surface a return flight from the last city.
                  </div>
                  <button
                    onClick={() => tb.splitBundleIntoStops(pickedDest, tb.startDate, tb.endDate)}
                    className="px-3 py-2 bg-gold text-navy text-sm font-bold rounded-lg hover:opacity-90 transition"
                  >
                    Split into {pickedDest.city.split(' + ').length} stops →
                  </button>
                </div>
              )}

              {/* Prescriptive guidance: how long to stay + what most people do.
                  Surfaced right after pick so non-travelers get direction
                  BEFORE committing to dates. */}
              {pickedDest && (
                <TripGuidanceCard
                  destination={pickedDest}
                  currentNights={Math.max(1, Math.round((new Date(tb.endDate) - new Date(tb.startDate)) / 86400000))}
                  onApplyPattern={(pattern) => tb.applyMultiCityPattern(pattern, pickedDest)}
                />
              )}

              {/* Pilot: AI plans the entire trip A-to-Z given vibe + points + cash cap */}
              {pickedDest && (
                <button
                  onClick={() => setAutoPlannerOpen(true)}
                  className="w-full bg-gradient-to-r from-teal/15 to-gold/15 border-2 border-teal/40 hover:border-teal rounded-2xl p-4 text-left transition group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal to-gold flex items-center justify-center text-navy font-black text-base shrink-0">P</div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-teal uppercase tracking-wider">✨ Let Pilot plan it</div>
                      <div className="font-bold text-sm text-brand-ink mt-0.5">Auto-plan {pickedDest.city} end-to-end</div>
                      <div className="text-[11px] text-brand-ink/70 mt-0.5 leading-snug">
                        Answer 4 vibe questions + set a cash cap. Pilot picks days per city, hotels, flight strategy, activities & dinners — with reasoning for every pick.
                      </div>
                    </div>
                    <div className="text-teal text-xl group-hover:translate-x-0.5 transition">→</div>
                  </div>
                </button>
              )}

              {/* Multi-city: additional stops picker */}
              {tb.isMultiCity && pickedDest && (
                <MultiCityStopsPicker
                  primary={pickedDest}
                  destinations={destinations}
                  tb={tb}
                />
              )}
            </div>
          )}

          {/* ═══ STEP 1: DATES ═══ */}
          {step === 'dates' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold">When are you going?</h2>
                <p className="text-brand-ink/70 mt-1">Pick your dates first. Everything else is built around them.</p>
              </div>

              {/* Trip shape selector — Round Trip vs Multi-City */}
              <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-3 sm:p-4">
                <div className="text-xs font-bold text-brand-ink/65 uppercase tracking-wider mb-2">Trip shape</div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <button
                    onClick={() => tb.setTripShape('round_trip')}
                    className={`text-left rounded-xl p-3 sm:p-4 border transition ${
                      tb.tripShape === 'round_trip'
                        ? 'bg-teal/15 border-teal/50 ring-2 ring-teal/30'
                        : 'bg-brand-ink/5 border-brand-ink/10 hover:border-brand-ink/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">✈</span>
                      <span className="font-bold text-sm">Round Trip</span>
                    </div>
                    <div className="text-[11px] text-brand-ink/65 mt-1">One destination. Same airport home.</div>
                  </button>
                  <button
                    onClick={() => tb.setTripShape('multi_city')}
                    className={`text-left rounded-xl p-3 sm:p-4 border transition ${
                      tb.tripShape === 'multi_city'
                        ? 'bg-gold/15 border-gold/50 ring-2 ring-gold/30'
                        : 'bg-brand-ink/5 border-brand-ink/10 hover:border-brand-ink/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🗺️</span>
                      <span className="font-bold text-sm">Multi-City</span>
                    </div>
                    <div className="text-[11px] text-brand-ink/65 mt-1">Multiple stops. Different hotels per city.</div>
                  </button>
                </div>
                {tb.isMultiCity && (
                  <div className="mt-3 bg-gold/10 border border-gold/20 rounded-lg p-2.5 text-[11px] text-brand-ink/75">
                    Pick your <strong>first stop</strong> next — you'll add more cities after.
                  </div>
                )}
              </div>

              <TripCalendar
                startDate={tb.startDate}
                endDate={tb.endDate}
                onDateChange={(s, e) => tb.setDates(s, e)}
              />

              {/* Full September intelligence */}
              {pickedDest && septemberContext[pickedDest.city] && (
                <SeptemberCard ctx={septemberContext[pickedDest.city]} city={pickedDest.city} />
              )}

              {/* General September tip when no destination picked yet */}
              {!pickedDest && tb.startDate && (
                <div className="bg-teal/10 border border-teal/20 rounded-xl p-4 text-sm text-brand-ink/75">
                  <span className="text-teal font-bold">September</span> is prime time — shoulder season in Europe means fewer crowds, warm weather, and harvest festivals.
                </div>
              )}

              {/* Trip length summary */}
              {tb.startDate && tb.endDate && (
                <div className="animate-fade-in bg-navy-light rounded-xl border border-teal/20 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-brand-ink">
                        {tb.nights} nights{pickedDest ? ` in ${pickedDest.city}` : ''}
                      </div>
                      <div className="text-xs text-brand-ink/65 mt-0.5">
                        {new Date(tb.startDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} &rarr; {new Date(tb.endDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    {pickedDest && (
                      <div className="text-right">
                        <div className="text-xs text-brand-ink/55">Estimated daily budget</div>
                        <div className="text-sm font-bold text-gold">${pickedDest.avg_daily_budget_min * tb.nights}-${pickedDest.avg_daily_budget_max * tb.nights} total</div>
                        <div className="text-[10px] text-brand-ink/45">(${pickedDest.avg_daily_budget_min}-${pickedDest.avg_daily_budget_max}/day x {tb.nights} nights)</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ STEP 3: FLIGHTS ═══ */}
          {step === 'flights' && !pickedDest && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Flights</h2>
              <p className="text-brand-ink/70">Pick a destination first so we can show flight options.</p>
              <button onClick={() => setStep('destination')} className="px-4 py-2 bg-teal text-navy font-bold rounded-lg">
                Choose destination →
              </button>
            </div>
          )}
          {step === 'flights' && pickedDest && !destDetail && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Loading flights for {pickedDest.city}…</h2>
              <div className="animate-shimmer h-32 rounded-xl" />
            </div>
          )}
          {step === 'flights' && destDetail && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold">{stepCopy.flights.heading(pickedDest.city)}</h2>
                <p className="text-brand-ink/70 mt-1">{stepCopy.flights.sub}</p>
              </div>
              {pickedDest && (
                <AiTip
                  context={`Flying to ${pickedDest.city}, ${pickedDest.country} from ${tb.userConfig.origin}. Have ${tb.amexTotal.toLocaleString()} Amex MR. ${tb.userConfig.cabinClass} class for ${tb.userConfig.travelers} traveler${tb.userConfig.travelers === 1 ? '' : 's'}.`}
                  question={`What's the best flight strategy for ${pickedDest.city}?`}
                />
              )}
              {/* Transfer Bonus Alert */}
              <TransferBonusAlert />
              {/* Metro-area airport quick-add checkbox — for users in NYC,
                  LA, SF, etc. who want to search ALL nearby airports at once
                  to find the cheapest awards across the whole region. */}
              {pickedDest && !tb.isMultiCity && (
                <MetroAirportPicker
                  primary={tb.userConfig.origin || 'JFK'}
                  selected={tb.userConfig.flightOrigins || [tb.userConfig.origin || 'JFK']}
                  onChange={(list) => tb.setUserConfig({ flightOrigins: list })}
                />
              )}

              {/* Live Award Availability from seats.aero */}
              {pickedDest && !tb.isMultiCity && (
                <LiveAvailability
                  origin={tb.userConfig.origin || 'JFK'}
                  origins={tb.userConfig.flightOrigins || undefined}
                  destinationCodes={pickedDest.airport_codes || []}
                  cabin={tb.userConfig.cabinClass === 'first' ? 'first' : 'business'}
                  onDataLoaded={(hasData) => setLiveHasData(hasData)}
                />
              )}

              {/* Multi-city: per-leg live availability — one search per flight leg.
                  Each leg picks INTO its own slot in tb.flightsByLeg so outbound,
                  inter-city, and return flights are tracked independently. */}
              {pickedDest && tb.isMultiCity && tb.legs.length > 0 && (
                <div className="space-y-4">
                  <div className="text-xs font-bold text-gold uppercase tracking-wider">Award availability per leg</div>
                  {tb.legs.map((leg, idx) => {
                    const cabin = tb.userConfig.cabinClass === 'first' ? 'first' : 'business';
                    const hasPicked = !!leg.flight;
                    return (
                      <details key={leg.index} className={`border rounded-xl overflow-hidden ${hasPicked ? 'bg-teal/5 border-teal/30' : 'bg-navy-light border-brand-ink/10'}`} open={idx === 0 || hasPicked}>
                        <summary className="cursor-pointer p-3 font-bold text-sm text-brand-ink/85 hover:bg-brand-ink/5 transition list-none flex items-center justify-between gap-2">
                          <span>{leg.label}</span>
                          <span className="text-[10px] font-normal flex items-center gap-2">
                            {hasPicked ? (
                              <>
                                <span className="text-teal font-semibold">✓ {leg.flight.airline_program} picked</span>
                                <button
                                  onClick={(e) => { e.preventDefault(); tb.clearLegFlight(leg.index); }}
                                  className="text-brand-ink/55 hover:text-brand-ink/85"
                                >Clear</button>
                              </>
                            ) : (
                              <span className="text-brand-ink/45">
                                {leg.kind === 'intercity' ? 'Inter-city — may also be train/ferry (see ribbon)' : 'Award flight'}
                              </span>
                            )}
                          </span>
                        </summary>
                        <div className="p-3 border-t border-brand-ink/10">
                          {leg.origin && leg.destinationCodes.length > 0 ? (
                            <LiveAvailability
                              origin={leg.origin}
                              destinationCodes={leg.destinationCodes}
                              cabin={cabin}
                              oneWay
                              legIndex={leg.index}
                              onDataLoaded={idx === 0 ? (hasData) => setLiveHasData(hasData) : undefined}
                            />
                          ) : (
                            <div className="text-xs text-brand-ink/55">Airport codes missing for this leg — search the airline directly.</div>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}
              {/* When no live data — show honest message + selectable saved options */}
              {!liveHasData && (
                <div className="space-y-3 mt-2">
                  <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-4">
                    <div className="text-sm font-medium text-brand-ink/75">Live pricing not available for this route yet</div>
                    <div className="text-xs text-brand-ink/65 mt-1">seats.aero hasn't cached this route for your dates. Pick a saved option below or search directly:</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {[...new Set((destDetail.redemption_options || []).map(r => r.airline_program))].map(program => (
                        <a key={program} href={bookingLinks[program]?.search || '#'} target="_blank" rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-teal/10 border border-teal/20 rounded-lg text-xs font-medium text-teal hover:bg-teal/20 transition">
                          Search {program} ↗
                        </a>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(destDetail.redemption_options || []).map(r => {
                      const selected = pickedFlight?.id === r.id;
                      const link = bookingLinks[r.airline_program];
                      return (
                        <div key={r.id} className={`rounded-xl border p-4 transition-all ${selected ? 'border-teal ring-2 ring-teal/30 bg-teal/5' : 'border-brand-ink/10 bg-navy-light hover:border-brand-ink/20'}`}>
                          {selected && <div className="text-teal text-[10px] font-bold uppercase mb-2">✓ Selected</div>}
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold">{r.routing_description}</div>
                              <div className="text-xs text-brand-ink/65 mt-0.5">via {r.airline_program} · {r.aircraft_type} · {r.seat_product}</div>
                              {r.transfer_time && <div className="text-[10px] text-brand-ink/55 mt-0.5">{r.transfer_time} transfer time</div>}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-lg font-black">{tb.scaleForTravelers(r.total_points_two_travelers).toLocaleString() || '—'}</div>
                              <div className="text-[10px] text-brand-ink/65">pts ({tb.travelersLabel})</div>
                              {r.total_estimated_taxes > 0 && <div className="text-[10px] text-brand-ink/55">+ ${tb.scaleForTravelers(r.total_estimated_taxes)} tax</div>}
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button onClick={() => tb.pickFlight(r)}
                              className={`flex-1 min-w-[110px] py-2 rounded-lg text-sm font-bold transition ${selected ? 'bg-teal/20 text-teal' : 'bg-teal text-navy hover:bg-teal-dark'}`}>
                              {selected ? 'Selected' : 'Select This Flight'}
                            </button>
                            {link && (
                              <a href={link.search} target="_blank" rel="noopener noreferrer"
                                className="flex-1 min-w-[110px] py-2 text-center bg-indigo-600/80 text-brand-cream rounded-lg text-sm font-medium hover:bg-indigo-600 transition">
                                Book on {r.airline_program} ↗
                              </a>
                            )}
                          </div>
                          {r.booking_steps && (
                            <details className="mt-2">
                              <summary className="text-[10px] text-brand-ink/55 cursor-pointer hover:text-brand-ink/70">Read more — booking steps</summary>
                              <div className="text-xs text-brand-ink/70 whitespace-pre-line mt-1.5 bg-brand-ink/5 rounded p-2">{r.booking_steps}</div>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Multi-city: per-leg flight summary */}
              {tb.isMultiCity && tb.legs.length > 0 && (
                <div className="animate-fade-in bg-gradient-to-r from-teal/10 to-blue-900/10 border border-teal/20 rounded-xl p-4 space-y-2">
                  <div className="text-xs font-bold text-teal uppercase tracking-wider">
                    Your flights ({tb.legs.length} legs)
                  </div>
                  {tb.legs.map((leg) => (
                    <div key={leg.index} className="flex items-center gap-3 text-sm">
                      <span className="w-20 text-xs text-brand-ink/65 font-medium flex-shrink-0 capitalize">{leg.kind === 'intercity' ? `Leg ${leg.index + 1}` : leg.kind}</span>
                      <span className="font-medium">{leg.origin} → {leg.destinationCodes[0]}</span>
                      <span className="ml-auto text-xs flex items-center gap-2">
                        {leg.flight ? (
                          <>
                            <span className="text-teal font-semibold">{leg.flight.airline_program}</span>
                            <span className="text-brand-ink/70">{tb.scaleForTravelers(leg.flight.total_points_two_travelers).toLocaleString()} pts</span>
                          </>
                        ) : leg.kind === 'intercity' ? (
                          <span className="text-brand-ink/55 italic">May not need a flight (see ribbon for train/ferry)</span>
                        ) : (
                          <span className="text-amber-400 font-medium">Not picked</span>
                        )}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-brand-ink/10 pt-2 text-xs text-brand-ink/70">
                    Total flight points: <span className="font-bold">{tb.flightPoints.toLocaleString()}</span> + <span className="font-bold">${tb.flightTaxes}</span> taxes ({tb.travelersLabel})
                  </div>
                </div>
              )}

              {/* Round-trip flight confirmation */}
              {pickedFlight && !tb.isMultiCity && (
                <div className="animate-fade-in bg-gradient-to-r from-teal/10 to-blue-900/10 border border-teal/20 rounded-xl p-4 space-y-3">
                  <div className="text-xs font-bold text-teal uppercase tracking-wider flex items-center justify-between">
                    <span>Your Flights (Round Trip)</span>
                    <button
                      onClick={() => tb.pickFlight(null)}
                      className="text-[10px] font-medium text-brand-ink/55 hover:text-brand-ink/85 normal-case tracking-normal"
                    >
                      Clear pick
                    </button>
                  </div>
                  {(() => {
                    // Direction-aware labels: detect which way the picked
                    // flight actually goes by comparing the routing to the
                    // user's home airport. If they picked from the Return
                    // tab by accident, the "Outbound" line still shows the
                    // real outbound direction (their home → destination)
                    // instead of just whatever they picked.
                    const route = pickedFlight.routing_description || '';
                    const pickedGoesHome = route.startsWith(`${pickedDest.airport_codes?.[0]} `)
                      || route.startsWith(`${pickedDest.airport_codes?.[0]} →`)
                      || route.toUpperCase().startsWith(pickedDest.airport_codes?.[0] + ' ');
                    // If the picked flight actually goes destination → home,
                    // it's the return; the outbound is the reverse.
                    const outboundRoute = pickedGoesHome ? reverseRoute(route) : route;
                    const returnRoute = pickedGoesHome ? route : reverseRoute(route);
                    return (
                      <>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 text-sm">
                            <span className="w-16 text-xs text-brand-ink/65 font-medium flex-shrink-0">Outbound</span>
                            <span className="font-medium">{outboundRoute}</span>
                            <span className="text-brand-ink/65 ml-auto text-xs">{pickedFlight.flight_duration_outbound}</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="w-16 text-xs text-brand-ink/65 font-medium flex-shrink-0">Return</span>
                            <span className="font-medium">{returnRoute}</span>
                            <span className="text-brand-ink/65 ml-auto text-xs">{pickedFlight.flight_duration_outbound}</span>
                          </div>
                        </div>
                        {pickedGoesHome && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-[11px] text-amber-300">
                            Heads up — you picked from the Return tab. The labels above are corrected, but you should also verify the <strong>{outboundRoute}</strong> direction has award seats before transferring points.
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div className="border-t border-brand-ink/10 pt-2 flex items-center justify-between text-sm">
                    <span className="text-brand-ink/70">Total: {tb.scaleForTravelers(pickedFlight.total_points_two_travelers).toLocaleString()} pts + ${tb.scaleForTravelers(pickedFlight.total_estimated_taxes)} taxes ({tb.travelersLabel})</span>
                    <span className="text-[10px] text-teal/60">{pickedFlight.requires_round_trip ? 'Must book as round-trip' : 'Book each direction separately'}</span>
                  </div>
                  <div className="text-[10px] text-brand-ink/55">
                    {pickedFlight.cabin_class === 'first' ? 'First' : 'Business'} class &middot; {pickedFlight.aircraft_type} &middot; {pickedFlight.seat_product} &middot; via {pickedFlight.airline_program}
                  </div>
                </div>
              )}

              {pickedFlight && (
                <div>
                  <button onClick={() => setExpandedSteps(expandedSteps ? null : pickedFlight.id)} className="text-sm text-teal font-medium hover:underline">
                    {expandedSteps ? 'Hide' : 'Show'} booking steps for {pickedFlight.airline_program}
                  </button>
                  {expandedSteps && (
                    <div className="mt-2 bg-brand-ink/5 rounded-lg p-4 text-sm text-brand-ink/80 whitespace-pre-line">{pickedFlight.booking_steps}</div>
                  )}
                </div>
              )}

              {/* Running cost after flight selection */}
              {pickedFlight && (
                <CostSoFar tb={tb} label="After flights (round trip)" />
              )}
            </div>
          )}

          {/* ═══ STEP 4: HOTEL ═══ */}
          {step === 'hotel' && !pickedDest && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Hotel</h2>
              <p className="text-brand-ink/70">Pick a destination first so we can show hotel options.</p>
              <button onClick={() => setStep('destination')} className="px-4 py-2 bg-teal text-navy font-bold rounded-lg">
                Choose destination →
              </button>
            </div>
          )}
          {step === 'hotel' && pickedDest && !destDetail && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Loading hotels for {pickedDest.city}…</h2>
              <div className="animate-shimmer h-32 rounded-xl" />
            </div>
          )}
          {step === 'hotel' && destDetail && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold">{stepCopy.hotel.heading(pickedDest.city)}</h2>
                <p className="text-brand-ink/70 mt-1">{stepCopy.hotel.sub}</p>
              </div>

              <AiTip
                context={`Picked destination ${pickedDest.city}. ${tb.isMultiCity ? `Multi-city: ${tb.stops.map(s => `${s.destination.city} (${s.nights}n)`).join(' → ')}.` : `${tb.nights} nights.`} Chase UR balance: ${tb.chaseTotal.toLocaleString()}. Already picked flight: ${pickedFlight?.airline_program || 'none yet'}.`}
                question={tb.isMultiCity
                  ? `For each city in my multi-city trip, what's the best hotel strategy — Hyatt redemptions vs. boutique cash vs. mixing? Suggest neighborhoods that match the trip's pace.`
                  : `What's the best hotel strategy for ${pickedDest.city}? Hyatt sweet spots vs. cash boutique?`}
              />

              {/* Multi-city: per-stop hotel pickers */}
              {tb.isMultiCity && tb.stops.length > 1 ? (
                <div className="space-y-3">
                  <div className="text-xs font-bold text-gold uppercase tracking-wider">Pick a hotel for each stop</div>
                  {computeStopDateRanges(tb).map((range, i) => {
                    const stop = tb.stops[i];
                    const isPrimary = i === 0;
                    return (
                      <StopHotelPicker
                        key={i}
                        destination={stop.destination}
                        nights={stop.nights}
                        checkin={range.checkin}
                        checkout={range.checkout}
                        picked={stop.hotel}
                        onPick={(h) => isPrimary ? tb.pickHotel(h) : tb.setStopHotel(i - 1, h)}
                        travelersLabel={tb.travelersLabel}
                        scaleForTravelers={tb.scaleForTravelers}
                        collapsedByDefault={i > 0}
                      />
                    );
                  })}
                </div>
              ) : (
                <>
                  {/* Live hotel award rates */}
                  {pickedDest && (
                    <LiveHotels
                      destinationCode={pickedDest.airport_codes?.[0]}
                      checkin={tb.startDate}
                      checkout={tb.endDate}
                    />
                  )}
                  <div className="space-y-3">
                    {(destDetail.hotel_options || []).map(h => {
                  const selected = pickedHotel?.id === h.id;
                  return (
                    <div
                      key={h.id}
                      className={`bg-navy-light rounded-xl border p-4 transition-all ${
                        selected ? 'border-indigo-500 ring-2 ring-indigo-500/30 shadow-lg' : 'border-brand-ink/10 hover:border-brand-ink/20'
                      }`}
                    >
                      {selected && <div className="text-indigo-300 text-[10px] font-bold uppercase mb-2">&#10003; Selected</div>}
                      {!selected && h.is_recommended && <div className="text-indigo-300 text-[10px] font-bold uppercase mb-2">Recommended</div>}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-lg">{h.hotel_name}</div>
                          <div className="flex items-center gap-2 text-xs text-brand-ink/70 mt-0.5 flex-wrap">
                            <span>via {h.hotel_program}</span>
                            <span className="text-brand-ink/45">&middot;</span>
                            <span>{h.nights} nights</span>
                            {h.neighborhood && <><span className="text-brand-ink/45">&middot;</span><span>{h.neighborhood}</span></>}
                            {h.star_rating && <span className="text-gold">{'★'.repeat(Math.floor(parseFloat(h.star_rating)))}</span>}
                          </div>
                          <div className="text-sm text-brand-ink/75 mt-2">{h.description}</div>
                          {h.amenities && h.amenities.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {h.amenities.slice(0, 5).map((a, j) => (
                                <span key={j} className="px-1.5 py-0.5 bg-brand-ink/5 rounded text-[10px] text-brand-ink/65 capitalize">{a}</span>
                              ))}
                              {h.amenities.length > 5 && <span className="text-[10px] text-brand-ink/45">+{h.amenities.length - 5}</span>}
                            </div>
                          )}
                          {h.cash_price_per_night > 0 && (
                            <div className="text-[10px] text-brand-ink/55 mt-1">Cash: ${h.cash_price_per_night}/night (${(h.cash_price_per_night * h.nights).toLocaleString()} total)</div>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xl font-black">{h.total_points?.toLocaleString()}</div>
                          <div className="text-xs text-brand-ink/65">{h.source_program === 'chase_ur' ? 'Chase' : 'Amex'} pts</div>
                          <div className="text-xs text-brand-ink/55">{h.points_per_night?.toLocaleString()}/night</div>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button onClick={() => tb.pickHotel(h)}
                          className={`flex-1 min-w-[100px] py-2 rounded-lg text-sm font-bold transition ${selected ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-600 text-brand-cream hover:bg-indigo-700'}`}>
                          {selected ? 'Selected' : 'Select Hotel'}
                        </button>
                        {bookingLinks[h.hotel_program] && (
                          <a href={bookingLinks[h.hotel_program].search} target="_blank" rel="noopener noreferrer"
                            className="flex-1 min-w-[100px] py-2 text-center bg-teal/15 text-teal rounded-lg text-sm font-medium hover:bg-teal/25 transition">
                            Book ↗
                          </a>
                        )}
                        <button onClick={() => { setDetailModal(h); setDetailType('hotel'); }}
                          className="px-3 py-2 bg-brand-ink/5 text-brand-ink/70 rounded-lg text-sm font-medium hover:bg-brand-ink/10 hover:text-brand-ink/85 transition">
                          Details
                        </button>
                      </div>
                      <HotelInsightCard
                        hotelProgram={h.hotel_program}
                        hotelName={h.hotel_name}
                      />
                    </div>
                  );
                })}
                  </div>
                </>
              )}
              <div className="bg-brand-ink/5 rounded-lg p-3 text-xs text-brand-ink/65">
                <strong className="text-brand-ink/75">Skip hotel?</strong> You can also pay cash or use the Chase travel portal. Not every destination has strong Hyatt options.
              </div>

              {/* Running cost after hotel */}
              <CostSoFar tb={tb} label="After flights + hotel" />
            </div>
          )}

          {/* ═══ STEP 4: ACTIVITIES ═══ */}
          {step === 'activities' && (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{stepCopy.activities.heading(pickedDest?.city)}</h2>
                  <p className="text-brand-ink/70 mt-1">{stepCopy.activities.sub}</p>
                </div>
                {pickedActivities.length > 0 && (
                  <div className="animate-scale-pop bg-gold/15 border border-gold/30 rounded-xl px-4 py-2 text-right flex-shrink-0">
                    <div className="text-[10px] text-gold/60 uppercase font-bold">Activities Total</div>
                    <div className="text-lg font-black text-gold">${tb.activityCash.toLocaleString()}</div>
                    <div className="text-[10px] text-brand-ink/55">{pickedActivities.length} selected</div>
                  </div>
                )}
              </div>

              <AiTip
                context={`Picking activities for ${tb.isMultiCity ? tb.stops.map(s => `${s.destination.city} (${s.nights}n)`).join(', ') : `${pickedDest.city} for ${tb.nights} nights`}. ${tb.travelers} traveler${tb.travelers === 1 ? '' : 's'}. Already picked: ${pickedFlight ? '1 flight' : 'no flight'}, ${pickedHotel || tb.stops.some(s => s.hotel) ? 'hotel(s) set' : 'no hotel yet'}.`}
                question={tb.isMultiCity
                  ? `Given my multi-city stops + nights, suggest a daily flow per city — must-do anchors + 1-2 hidden gems + meal pacing.`
                  : `What's a sensible 3-pace plan for ${pickedDest.city} (anchor sight + neighborhood walk + sunset spot)?`}
              />

              {/* Auto-generated day-by-day plan suggestion */}
              <DailyPlanSuggester />

              {/* Seasonal tips for activities */}
              {pickedDest && septemberContext[pickedDest.city] && (
                <div className="bg-gradient-to-r from-teal/10 to-blue-900/10 border border-teal/20 rounded-xl p-4 animate-fade-in">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">*</span>
                    <div>
                      <div className="text-xs font-bold text-teal uppercase tracking-wider mb-1">September in {pickedDest.city}</div>
                      <div className="text-sm text-brand-ink/80">{septemberContext[pickedDest.city].bestFor}</div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {septemberContext[pickedDest.city].inSeason.map((s, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 bg-teal/15 text-teal rounded-full">{s}</span>
                        ))}
                      </div>
                      {septemberContext[pickedDest.city].events.length > 0 && (
                        <div className="mt-2 text-xs text-gold/80">
                          {septemberContext[pickedDest.city].events.map((e, i) => (
                            <div key={i}>* {e}</div>
                          ))}
                        </div>
                      )}
                      {septemberContext[pickedDest.city].warning && (
                        <div className="mt-2 text-xs text-gold bg-gold/10 rounded px-2 py-1">{septemberContext[pickedDest.city].warning}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Multi-city: per-stop activities pickers */}
              {tb.isMultiCity && tb.stops.length > 1 ? (
                <div className="space-y-3">
                  <div className="text-xs font-bold text-gold uppercase tracking-wider">Activities per stop</div>
                  {tb.stops.map((stop, i) => {
                    const isPrimary = i === 0;
                    return (
                      <StopActivitiesPicker
                        key={i}
                        destination={stop.destination}
                        picked={stop.activities}
                        onToggle={(a) => isPrimary ? tb.toggleActivity(a) : tb.toggleStopActivity(i - 1, a)}
                        scaleForTravelers={tb.scaleForTravelers}
                        travelersLabel={tb.travelersLabel}
                        collapsedByDefault={i > 0}
                      />
                    );
                  })}
                </div>
              ) : (
              <div className="space-y-3">
                {allActivities.map(a => {
                  const selected = pickedActivities.some(x => x.id === a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() => tb.toggleActivity(a)}
                      className={`w-full text-left bg-navy-light rounded-xl border p-4 transition-all ${
                        selected ? 'border-teal ring-1 ring-teal/30' : 'border-brand-ink/10 hover:border-brand-ink/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] ${selected ? 'border-teal bg-teal text-navy' : 'border-brand-ink/20'}`}>
                              {selected && '✓'}
                            </span>
                            <span className="font-semibold">{a.name}</span>
                            {a.rating && <span className="text-xs text-gold">{a.rating} ★</span>}
                          </div>
                          <div className="text-xs text-brand-ink/65 mt-1 ml-7">{a.duration} &middot; {a.category.replace('_', ' ')}</div>
                          <div className="text-sm text-brand-ink/75 mt-1 ml-7">{a.description}</div>
                          {a.booking_tip && <div className="text-[11px] text-teal/60 mt-1 ml-7">Tip: {a.booking_tip}</div>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-bold text-gold">{a.price_for_two === 0 ? 'Free' : `$${tb.scaleForTravelers(a.price_for_two)}`}</div>
                          <div className="text-[10px] text-brand-ink/55">for {tb.travelersLabel}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* ═══ STEP 5: RESTAURANTS ═══ */}
          {step === 'food' && (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{stepCopy.food.heading()}</h2>
                <p className="text-brand-ink/70 mt-1">{stepCopy.food.sub}</p>
                </div>
                {pickedRestaurants.length > 0 && (
                  <div className="animate-scale-pop bg-gold/15 border border-gold/30 rounded-xl px-4 py-2 text-right flex-shrink-0">
                    <div className="text-[10px] text-gold/60 uppercase font-bold">Food Total</div>
                    <div className="text-lg font-black text-gold">${tb.restaurantCash.toLocaleString()}</div>
                    <div className="text-[10px] text-brand-ink/55">{pickedRestaurants.length} spots</div>
                  </div>
                )}
              </div>

              <AiTip
                context={`Restaurants for ${tb.isMultiCity ? tb.stops.map(s => s.destination.city).join(' + ') : pickedDest.city}. ${tb.travelers} traveler${tb.travelers === 1 ? '' : 's'}. ${pickedActivities.length} activities already picked.`}
                question={tb.isMultiCity
                  ? `One must-book restaurant per city in my itinerary, plus a casual breakfast/lunch spot per city. Mix of local + Michelin if budget allows.`
                  : `Two reservation-worthy dinners + a memorable hole-in-the-wall lunch for ${pickedDest.city}?`}
              />

              {/* What's in season for food */}
              {pickedDest && septemberContext[pickedDest.city] && (
                <div className="bg-gradient-to-r from-gold/10 to-orange-900/10 border border-gold/20 rounded-xl p-3 animate-fade-in">
                  <div className="text-xs font-bold text-gold uppercase tracking-wider mb-1">What's in season</div>
                  <div className="flex flex-wrap gap-1.5">
                    {septemberContext[pickedDest.city].inSeason.map((s, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-gold/15 text-gold/80 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Multi-city: per-stop restaurant pickers */}
              {tb.isMultiCity && tb.stops.length > 1 ? (
                <div className="space-y-3">
                  <div className="text-xs font-bold text-gold uppercase tracking-wider">Restaurants per stop</div>
                  {tb.stops.map((stop, i) => {
                    const isPrimary = i === 0;
                    return (
                      <StopRestaurantsPicker
                        key={i}
                        destination={stop.destination}
                        picked={stop.restaurants}
                        onToggle={(r) => isPrimary ? tb.toggleRestaurant(r) : tb.toggleStopRestaurant(i - 1, r)}
                        scaleForTravelers={tb.scaleForTravelers}
                        travelersLabel={tb.travelersLabel}
                        collapsedByDefault={i > 0}
                      />
                    );
                  })}
                </div>
              ) : (
              <div className="space-y-3">
                {allRestaurants.map(r => {
                  const selected = pickedRestaurants.some(x => x.id === r.id);
                  return (
                    <button
                      key={r.id}
                      onClick={() => {
                        tb.toggleRestaurant(r);
                      }}
                      className={`w-full text-left bg-navy-light rounded-xl border p-4 transition-all ${
                        selected ? 'border-gold ring-1 ring-gold/30' : 'border-brand-ink/10 hover:border-brand-ink/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] ${selected ? 'border-gold bg-gold text-navy' : 'border-brand-ink/20'}`}>
                              {selected && '✓'}
                            </span>
                            <span className="font-semibold">{r.name}</span>
                            <span className="text-xs text-brand-ink/55">{r.price_range}</span>
                            {r.rating && <span className="text-xs text-gold">{r.rating} ★</span>}
                          </div>
                          <div className="text-xs text-brand-ink/65 mt-1 ml-7">{r.cuisine} &middot; {r.neighborhood} &middot; {r.category.replace('_', ' ')}</div>
                          <div className="text-sm text-brand-ink/75 mt-1 ml-7">{r.description}</div>
                          {r.booking_tip && <div className="text-[11px] text-gold/60 mt-1 ml-7">Tip: {r.booking_tip}</div>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-bold text-gold">${tb.scaleForTravelers(r.avg_meal_for_two)}</div>
                          <div className="text-[10px] text-brand-ink/55">for {tb.travelersLabel}</div>
                        </div>
                      </div>
                      <RestaurantBookingPanel restaurant={r} />
                    </button>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* ═══ STEP 6: REVIEW ═══ */}
          {step === 'review' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold">{stepCopy.review.heading(pickedDest?.city || 'Europe')}</h2>
                <p className="text-brand-ink/70 mt-1">{stepCopy.review.sub}</p>
              </div>

              <AiTip
                context={`Final review. ${tb.isMultiCity ? `Multi-city: ${tb.stops.map(s => `${s.destination.city} (${s.nights}n${s.hotel ? `, ${s.hotel.hotel_name}` : ', no hotel'})`).join(' → ')}.` : `${pickedDest?.city}, ${tb.nights}n, ${pickedHotel ? pickedHotel.hotel_name : 'no hotel'}.`} Flight: ${pickedFlight ? `${pickedFlight.airline_program} ${tb.flightPoints.toLocaleString()} pts` : 'none picked'}. ${pickedActivities.length} activities, ${pickedRestaurants.length} restaurants. Cash total: $${tb.totalCash}.`}
                question={`Critique this trip. Anything I should swap, add, or worry about before I transfer points and book? Be specific about risks (availability, fuel surcharges, restaurant booking windows).`}
              />

              {/* Saved mockups comparison */}
              {tb.mockups.length > 0 && (
                <MockupCompare
                  onLoad={(m) => { tb.loadMockup(m); }}
                />
              )}

              {/* Destination */}
              <ReviewCard title="Destination" complete={!!pickedDest} onEdit={() => setStep('destination')}>
                {pickedDest && (
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                      <DestImage city={pickedDest.city} />
                    </div>
                    <div>
                      <div className="font-bold text-lg">{pickedDest.city}, {pickedDest.country}</div>
                      <div className="text-xs text-brand-ink/65">September 2026 &middot; {pickedDest.weather_high_f}°F &middot; ${pickedDest.avg_daily_budget_min}-{pickedDest.avg_daily_budget_max}/day</div>
                    </div>
                  </div>
                )}
              </ReviewCard>

              {/* Flight */}
              <ReviewCard title="Flights (Round Trip)" complete={!!pickedFlight} onEdit={() => setStep('flights')}>
                {pickedFlight && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pickedFlight.cabin_class === 'first' ? 'bg-gold/20 text-gold' : 'bg-indigo-500/20 text-indigo-300'}`}>
                        {pickedFlight.cabin_class}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-900/50 rounded text-blue-300 text-xs">Amex MR &rarr; {pickedFlight.airline_program}</span>
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-brand-ink/55 w-10">OUT</span>
                        <span className="font-medium">{pickedFlight.routing_description}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-brand-ink/55 w-10">RET</span>
                        <span className="font-medium">{reverseRoute(pickedFlight.routing_description)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-brand-ink/75 flex-wrap">
                      <span>{tb.scaleForTravelers(pickedFlight.total_points_two_travelers).toLocaleString()} pts (RT, {tb.travelersLabel})</span>
                      <span>${tb.scaleForTravelers(pickedFlight.total_estimated_taxes)} taxes</span>
                      <span>{pickedFlight.flight_duration_outbound} each way</span>
                      <span>{pickedFlight.is_nonstop ? 'Nonstop' : `${pickedFlight.num_stops} stop`}</span>
                    </div>
                    <div className="text-xs text-brand-ink/65">{pickedFlight.aircraft_type} &middot; {pickedFlight.seat_product}</div>
                  </div>
                )}
              </ReviewCard>

              {/* Hotel */}
              <ReviewCard title="Hotel" complete={!!pickedHotel} onEdit={() => setStep('hotel')}>
                {pickedHotel && (
                  <div>
                    <div className="font-semibold">{pickedHotel.hotel_name}</div>
                    <div className="text-sm text-brand-ink/75">{pickedHotel.nights} nights &middot; {pickedHotel.total_points?.toLocaleString()} Chase pts</div>
                  </div>
                )}
              </ReviewCard>

              {/* Activities */}
              <ReviewCard title={`Activities (${pickedActivities.length})`} complete={pickedActivities.length > 0} onEdit={() => setStep('activities')}>
                {pickedActivities.map(a => (
                  <div key={a.id} className="flex justify-between py-1 text-sm">
                    <span>{a.name}</span>
                    <span className="text-gold">${a.price_for_two}</span>
                  </div>
                ))}
              </ReviewCard>

              {/* Restaurants */}
              <ReviewCard title={`Restaurants (${pickedRestaurants.length})`} complete={pickedRestaurants.length > 0} onEdit={() => setStep('food')}>
                {pickedRestaurants.map(r => (
                  <div key={r.id} className="flex justify-between py-1 text-sm">
                    <span>{r.name} <span className="text-brand-ink/55">{r.price_range}</span></span>
                    <span className="text-gold">${r.avg_meal_for_two}</span>
                  </div>
                ))}
              </ReviewCard>

              {/* Interactive Day-by-Day Builder */}
              {pickedDest && (pickedActivities.length > 0 || pickedRestaurants.length > 0) && (
                <DayByDayBuilder
                  activities={pickedActivities}
                  restaurants={pickedRestaurants}
                  startDate={tb.startDate}
                  endDate={tb.endDate}
                  destinationCity={pickedDest.city}
                />
              )}

              {/* Neighborhood Map View */}
              {pickedDest && (pickedActivities.length > 0 || pickedRestaurants.length > 0) && (
                <MapView
                  activities={pickedActivities}
                  restaurants={pickedRestaurants}
                  destinationCity={pickedDest.city}
                />
              )}

              {/* Multi-City Route Planner */}
              {destinations.length > 0 && (
                <MultiCityPlanner destinations={destinations} />
              )}

              {/* Day-by-Day Summary */}
              {pickedDest && (
                <DayByDay
                  destination={pickedDest}
                  flight={pickedFlight}
                  hotel={pickedHotel}
                  activities={pickedActivities}
                  restaurants={pickedRestaurants}
                  startDate={tb.startDate}
                  endDate={tb.endDate}
                  nights={tb.nights}
                />
              )}

              {/* Packing List */}
              {pickedDest && (
                <PackingList
                  destination={pickedDest}
                  activities={pickedActivities}
                  flight={pickedFlight}
                />
              )}

              {/* Share & Export */}
              <div className="flex flex-wrap gap-3">
                <ShareTrip />
                <CalendarExport />
              </div>

              {/* Booking Steps — round trip */}
              {pickedFlight && !tb.isMultiCity && (
                <div id="booking-checklist" className="bg-navy-light rounded-xl border border-teal/30 p-5">
                  <h3 className="font-bold text-teal mb-3">Booking Checklist</h3>
                  <div className="space-y-3 text-sm text-brand-ink/80">
                    <BookStep num={1} text={`Create an ${pickedFlight.airline_program} account for each traveler`} link={bookingLinks[pickedFlight.airline_program]?.signup} linkLabel="Sign up" />
                    <BookStep num={2} text={`Search ${pickedFlight.airline_program} for availability on your dates`} link={bookingLinks[pickedFlight.airline_program]?.search} linkLabel="Search flights" />
                    <BookStep num={3} text={`Confirm ${tb.travelers} ${pickedFlight.cabin_class === 'first' ? 'first' : 'business'} class seat${tb.travelers === 1 ? '' : 's'} ${tb.travelers === 1 ? 'is' : 'are'} available — DO NOT transfer until you see seats`} warning />
                    <BookStep num={4} text={`Transfer ${tb.scaleForTravelers(pickedFlight.total_points_two_travelers).toLocaleString()} ${pickedFlight.source_program === 'chase_ur' ? 'Chase UR' : 'Amex MR'} → ${pickedFlight.airline_program} (${pickedFlight.transfer_time || 'instant'})`} highlight link={bookingLinks[pickedFlight.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr']?.transfer} linkLabel={`Open ${pickedFlight.source_program === 'chase_ur' ? 'Chase' : 'Amex'} transfers`} />
                    <BookStep num={5} text="Book flights immediately after transfer lands" />
                    <BookStep num={6} text="Select your seats" />
                    {pickedHotel && (
                      <>
                        <BookStep num={7} text={`Transfer ${pickedHotel.total_points?.toLocaleString()} Chase UR → ${pickedHotel.hotel_program}`} highlight link={bookingLinks.chase_ur?.transfer} linkLabel="Open Chase transfers" />
                        <BookStep num={8} text={`Book ${pickedHotel.hotel_name} with points`} link={bookingLinks[pickedHotel.hotel_program]?.search} linkLabel="Search hotels" />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Booking Steps — multi-city: one section per stop/leg */}
              {tb.isMultiCity && tb.stops.length > 0 && (
                <MultiCityBookingChecklist tb={tb} />
              )}
            </div>
          )}

          </div>{/* end animate-step-enter */}

          {/* Navigation — sticky to bottom so it's always reachable while scrolling */}
          <div className="sticky bottom-[60px] md:bottom-3 z-30 flex items-center justify-between mt-8 px-3 -mx-3 py-3 border-t border-brand-ink/10 bg-brand-cream/95 backdrop-blur-sm rounded-t-xl shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
            {step !== 'destination' ? (
              <button onClick={goBack} className="px-5 py-2.5 text-brand-ink/70 hover:text-brand-ink text-sm font-medium transition">&larr; Back</button>
            ) : <div />}
            {step !== 'review' ? (
              <button
                onClick={goNext}
                disabled={!canProgress()}
                className="px-6 py-2.5 bg-gradient-to-r from-teal to-teal-dark text-navy font-bold rounded-xl text-sm shadow-lg shadow-teal/20 hover:opacity-90 transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {step === 'dates' ? `${tb.nights || '?'} nights — let's pick where` :
                 step === 'destination' ? `Let's go to ${pickedDest?.city || '...'}` :
                 step === 'flights' ? (pickedFlight ? 'Flights booked — next' : 'Skip flights — next') :
                 step === 'activities' || step === 'food' ? 'Continue (skip is OK)' :
                 'Next'} &rarr;
              </button>
            ) : (
              <button
                onClick={() => {
                  setConfettiKey(k => k + 1);
                  document.getElementById('booking-checklist')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-3 bg-gradient-to-r from-teal to-emerald-500 text-navy font-extrabold rounded-xl text-base shadow-lg shadow-teal/30 hover:opacity-90 transition animate-pulse-glow"
              >
                Let's Book This Trip &#9992;&#65039;
              </button>
            )}
          </div>
        </div>

        {/* ═══ TRIP SUMMARY SIDEBAR ═══ */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-24 space-y-4">
            <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-brand-ink/70 uppercase tracking-wider">Your Trip So Far</div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${tb.itemCount > 0 ? 'bg-teal animate-pulse' : 'bg-brand-ink/20'}`} />
                  <span className="text-[10px] text-brand-ink/55">{tb.itemCount} items</span>
                </div>
              </div>

              {/* Points with animated bars */}
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-blue-300 font-medium">Amex MR (flights)</span>
                    <span className={`font-medium ${tb.amexRemaining < 0 ? 'text-danger' : 'text-brand-ink/70'}`}>
                      <AnimatedCounter value={tb.amexRemaining} duration={400} /> left
                    </span>
                  </div>
                  <div className="h-2.5 bg-brand-ink/10 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${tb.amexRemaining < 0 ? 'bg-gradient-to-r from-danger to-red-400' : 'bg-gradient-to-r from-blue-500 to-blue-400'}`} style={{ width: `${Math.min((tb.flightPoints / tb.amexTotal) * 100, 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-indigo-300 font-medium">Chase UR (hotel)</span>
                    <span className={`font-medium ${tb.chaseRemaining < 0 ? 'text-danger' : 'text-brand-ink/70'}`}>
                      <AnimatedCounter value={tb.chaseRemaining} duration={400} /> left
                    </span>
                  </div>
                  <div className="h-2.5 bg-brand-ink/10 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${tb.chaseRemaining < 0 ? 'bg-gradient-to-r from-danger to-red-400' : 'bg-gradient-to-r from-indigo-500 to-indigo-400'}`} style={{ width: `${Math.min((tb.hotelPoints / tb.chaseTotal) * 100, 100)}%` }} />
                  </div>
                </div>
              </div>

              {/* Picks */}
              <div className="space-y-2 text-sm">
                <SidebarItem label="Destination" value={tb.isMultiCity ? tb.stops.map(s => s.destination.city).join(' → ') : pickedDest?.city} />
                <SidebarItem label="Flight" value={pickedFlight ? `${pickedFlight.airline_program}` : null} sub={pickedFlight ? `${tb.flightPoints.toLocaleString()} pts` : null} />
                {/* Hotels — every stop with one picked, with summed totals */}
                {tb.isMultiCity ? (
                  <>
                    {tb.stops.map((s, i) => (
                      <SidebarItem
                        key={i}
                        label={`Hotel · ${s.destination.city}`}
                        value={s.hotel?.hotel_name || (<span className="text-amber-400">No hotel picked</span>)}
                        sub={s.hotel ? `${s.hotel.total_points?.toLocaleString()} pts · ${s.nights}n` : `${s.nights}n`}
                      />
                    ))}
                  </>
                ) : (
                  <SidebarItem label="Hotel" value={pickedHotel?.hotel_name} sub={pickedHotel ? `${tb.hotelPoints.toLocaleString()} pts` : null} />
                )}
                {/* Activities — show per-stop totals */}
                {tb.isMultiCity ? (
                  tb.stops.map((s, i) => (
                    <SidebarItem
                      key={'act' + i}
                      label={`Activities · ${s.destination.city}`}
                      value={s.activities?.length > 0 ? `${s.activities.length} planned` : null}
                      sub={s.activities?.length > 0 ? `$${tb.scaleForTravelers(s.activities.reduce((a, x) => a + (x.price_for_two || 0), 0))}` : null}
                    />
                  ))
                ) : (
                  <SidebarItem label="Activities" value={pickedActivities.length > 0 ? `${pickedActivities.length} planned` : null} sub={tb.activityCash > 0 ? `$${tb.activityCash}` : null} />
                )}
                {tb.isMultiCity ? (
                  tb.stops.map((s, i) => (
                    <SidebarItem
                      key={'rest' + i}
                      label={`Food · ${s.destination.city}`}
                      value={s.restaurants?.length > 0 ? `${s.restaurants.length} spots` : null}
                      sub={s.restaurants?.length > 0 ? `$${tb.scaleForTravelers(s.restaurants.reduce((a, x) => a + (x.avg_meal_for_two || 0), 0))}` : null}
                    />
                  ))
                ) : (
                  <SidebarItem label="Restaurants" value={pickedRestaurants.length > 0 ? `${pickedRestaurants.length} spots` : null} sub={tb.restaurantCash > 0 ? `$${tb.restaurantCash}` : null} />
                )}
              </div>

              {/* Totals */}
              <div className="border-t border-brand-ink/10 pt-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Points used</span>
                  <span className="font-semibold"><AnimatedCounter value={tb.totalPoints} duration={400} /></span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Points remaining</span>
                  <span className={`font-semibold ${(tb.amexRemaining + tb.chaseRemaining) < 0 ? 'text-danger' : 'text-teal'}`}>
                    <AnimatedCounter value={tb.amexRemaining + tb.chaseRemaining} duration={400} />
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65" title="Out-of-pocket cash: flight taxes + activities + restaurants. Hotels paid in points don't count here.">Cash total ⓘ</span>
                  <span className="font-bold text-gold">$<AnimatedCounter value={tb.totalCash} duration={400} /></span>
                </div>
                <div className="text-[10px] text-brand-ink/45 leading-tight pt-0.5">
                  Cash = flight taxes + activities + restaurants (your hotels are paid in points)
                </div>
                {tb.isMultiCity && (
                  <div className="text-[10px] text-amber-400/85 leading-tight pt-1">
                    Multi-city: search each leg on the Flights step. The total above is for the outbound flight only — add inter-city + return legs as you book them.
                  </div>
                )}
              </div>
            </div>

            {/* Save Mockup */}
            {pickedDest && (
              <div className="space-y-2">
                {!showSaveMockup ? (
                  <button
                    onClick={() => setShowSaveMockup(true)}
                    className={`w-full py-2.5 text-sm font-medium rounded-xl border transition ${justSaved ? 'bg-teal/20 border-teal/30 text-teal' : 'bg-brand-ink/5 border-brand-ink/10 text-brand-ink/70 hover:bg-brand-ink/10 hover:text-brand-ink/85'}`}
                  >
                    {justSaved ? 'Saved!' : 'Save as Mockup Trip'}
                  </button>
                ) : (
                  <div className="bg-navy-light rounded-xl border border-teal/30 p-3 animate-scale-pop space-y-2">
                    <input
                      type="text"
                      value={mockupName}
                      onChange={e => setMockupName(e.target.value)}
                      placeholder={`${pickedDest.city} Plan`}
                      className="w-full bg-brand-ink/5 border border-brand-ink/10 rounded-lg px-3 py-2 text-sm text-brand-ink placeholder-brand-ink/40 focus:outline-none focus:border-teal"
                      autoFocus
                      onKeyDown={e => e.key === 'Enter' && handleSaveMockup()}
                    />
                    <div className="flex gap-2">
                      <button onClick={handleSaveMockup} className="flex-1 py-2 bg-teal text-navy text-xs font-bold rounded-lg">Save</button>
                      <button onClick={() => setShowSaveMockup(false)} className="px-3 py-2 text-xs text-brand-ink/65 hover:text-brand-ink/75">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Saved Mockups */}
            {tb.mockups.length > 0 && (
              <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-3 space-y-2">
                <div className="text-[10px] font-bold text-brand-ink/65 uppercase tracking-wider">Saved Mockups ({tb.mockups.length})</div>
                {tb.mockups.map(m => (
                  <div key={m.id} className="flex items-center justify-between py-1.5 px-2 bg-brand-ink/5 rounded-lg group">
                    <button
                      onClick={() => { tb.loadMockup(m); setStep('review'); }}
                      className="text-left flex-1"
                    >
                      <div className="text-xs font-medium text-brand-ink/80 group-hover:text-brand-ink transition">{m.name}</div>
                      <div className="text-[10px] text-brand-ink/55">{m.costs.totalPoints.toLocaleString()} pts + ${m.costs.totalCash}</div>
                    </button>
                    <button
                      onClick={() => tb.deleteMockup(m.id)}
                      className="text-brand-ink/45 hover:text-danger text-xs ml-2 transition"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Transfer warning */}
            {pickedFlight && (
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-3 text-[11px] text-gold/80">
                &#9888; Remember: don't transfer points until you confirm availability on {pickedFlight.airline_program}'s website.
              </div>
            )}

            {/* Clear trip */}
            {pickedDest && (
              <button
                onClick={() => { tb.clearTrip(); setStep('destination'); }}
                className="w-full py-2 text-[11px] text-brand-ink/45 hover:text-brand-ink/65 transition"
              >
                Start Over
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <PlanDetailModal item={detailModal} type={detailType} onClose={() => { setDetailModal(null); setDetailType(null); }} />
      )}
    </div>
  );
}

// Filter the destination catalog by whether their best_months overlap the
// trip's start month. Seed data uses 3-letter abbreviations ("Sep") so we
// check against both abbreviated and full month names to be tolerant.
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function filterDestinationsByMonth(destinations, startDate) {
  if (!startDate) return destinations;
  const monthIdx = new Date(startDate + 'T12:00:00').getMonth();
  const monthFull = MONTHS_FULL[monthIdx];
  const monthAbbr = MONTHS_ABBR[monthIdx];
  const anyHasMonths = destinations.some(d => d.best_months && d.best_months.length > 0);
  if (!anyHasMonths) return destinations;
  return destinations.filter(d => {
    if (!d.best_months || d.best_months.length === 0) return true;
    return d.best_months.includes(monthFull) || d.best_months.includes(monthAbbr);
  });
}

// Banner above the destination grid showing the user the filter that's in
// effect and a count of in-season destinations.
function DestinationFilterBar({ tb, destinations }) {
  if (!tb.startDate) return null;
  const monthIdx = new Date(tb.startDate + 'T12:00:00').getMonth();
  const monthName = MONTHS_FULL[monthIdx];
  const total = destinations.length;
  const filtered = filterDestinationsByMonth(destinations, tb.startDate).length;
  const hidden = total - filtered;
  return (
    <div className="bg-brand-ink/5 border border-brand-ink/10 rounded-lg p-2.5 text-xs flex items-center gap-2">
      <span className="font-bold text-brand-ink/75">In season for {monthName}:</span>
      <span className="text-brand-ink/85">{filtered} destinations</span>
      {hidden > 0 && (
        <span className="text-brand-ink/55 ml-auto">{hidden} off-season hidden</span>
      )}
    </div>
  );
}

/* ── Multi-City Booking Checklist ──
 * Enumerates every actual booking action across the trip in order:
 *   1) Outbound flight + transfer
 *   2) Each stop's hotel (with the right source program)
 *   3) Each inter-city leg (flight or train/ferry from the transport map)
 *   4) Per-city activities the user picked
 *   5) Return flight + transfer
 */
function MultiCityBookingChecklist({ tb }) {
  const home = tb.userConfig.origin || 'JFK';
  const stops = tb.stops;
  const legs = tb.legs;
  const ranges = computeStopDateRanges(tb);
  let stepNum = 1;
  const items = [];

  // Outbound flight (leg 0)
  const outboundLeg = legs.find(l => l.kind === 'outbound');
  if (outboundLeg?.flight) {
    const f = outboundLeg.flight;
    const src = f.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr';
    const srcLabel = src === 'chase_ur' ? 'Chase UR' : 'Amex MR';
    items.push({
      n: stepNum++,
      text: `Outbound · ${home} → ${stops[0].destination.city}: Confirm ${tb.travelers} seat${tb.travelers === 1 ? '' : 's'} on ${f.airline_program} BEFORE transferring`,
      warning: true,
    });
    items.push({
      n: stepNum++,
      text: `Transfer ${tb.scaleForTravelers(f.total_points_two_travelers).toLocaleString()} ${srcLabel} → ${f.airline_program} (${f.transfer_time || 'instant'}), then book`,
      highlight: true,
      link: bookingLinks[src]?.transfer,
      linkLabel: `Open ${srcLabel} transfers`,
    });
  } else {
    items.push({
      n: stepNum++,
      text: `Outbound · ${home} → ${stops[0].destination.city}: pick a flight on the Flights step first`,
      warning: true,
    });
  }

  // For each stop: hotel + activities + leg to next stop
  stops.forEach((stop, i) => {
    const range = ranges[i] || {};
    const dateStr = range.checkin && range.checkout
      ? ` (${new Date(range.checkin + 'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}-${new Date(range.checkout + 'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})})`
      : '';

    // Hotel
    if (stop.hotel) {
      const hSrc = stop.hotel.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr';
      const hSrcLabel = hSrc === 'chase_ur' ? 'Chase UR' : 'Amex MR';
      items.push({
        n: stepNum++,
        text: `Stop ${i + 1} hotel${dateStr}: Transfer ${stop.hotel.total_points?.toLocaleString()} ${hSrcLabel} → ${stop.hotel.hotel_program}`,
        highlight: true,
        link: bookingLinks[hSrc]?.transfer,
        linkLabel: `Open ${hSrcLabel} transfers`,
      });
      items.push({
        n: stepNum++,
        text: `Stop ${i + 1} hotel: Book ${stop.hotel.hotel_name} (${stop.nights}n) in ${stop.destination.city}`,
        link: bookingLinks[stop.hotel.hotel_program]?.search,
        linkLabel: 'Search hotel',
      });
    } else {
      items.push({
        n: stepNum++,
        text: `Stop ${i + 1} (${stop.destination.city}, ${stop.nights}n${dateStr}): No hotel picked yet — go to Hotel step`,
        warning: true,
      });
    }

    // Activities for this stop
    if (stop.activities && stop.activities.length > 0) {
      items.push({
        n: stepNum++,
        text: `${stop.destination.city}: Book ${stop.activities.length} activit${stop.activities.length === 1 ? 'y' : 'ies'} — ${stop.activities.slice(0, 3).map(a => a.name).join(', ')}${stop.activities.length > 3 ? '…' : ''}`,
      });
    }
    if (stop.restaurants && stop.restaurants.length > 0) {
      items.push({
        n: stepNum++,
        text: `${stop.destination.city}: ${stop.restaurants.length} restaurant${stop.restaurants.length === 1 ? '' : 's'} on the list — book reservations 1-2 weeks ahead via OpenTable / SevenRooms / Google`,
      });
    }

    // Inter-city leg to next stop — either picked flight OR transport advice
    const nextStop = stops[i + 1];
    if (nextStop) {
      const interLeg = legs.find(l => l.kind === 'intercity' && l.index === i + 1);
      if (interLeg?.flight) {
        const f = interLeg.flight;
        const src = f.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr';
        const srcLabel = src === 'chase_ur' ? 'Chase UR' : 'Amex MR';
        items.push({
          n: stepNum++,
          text: `${stop.destination.city} → ${nextStop.destination.city}: Confirm ${tb.travelers} seat${tb.travelers === 1 ? '' : 's'} on ${f.airline_program}, transfer ${tb.scaleForTravelers(f.total_points_two_travelers).toLocaleString()} ${srcLabel}, then book`,
          highlight: true,
          link: bookingLinks[src]?.transfer,
          linkLabel: `Open ${srcLabel} transfers`,
        });
      } else {
        const t = getTransportInfo(stop.destination.city, nextStop.destination.city);
        if (t) {
          const effortLabel = t.effort ? ` [${t.effort.toUpperCase()}]` : '';
          items.push({
            n: stepNum++,
            text: `${stop.destination.city} → ${nextStop.destination.city}${effortLabel}: ${t.mode} (~${t.duration}, ${t.cost}). ${t.note}`,
          });
        }
      }
    }
  });

  // Return flight: from last stop → home (uses its own leg index now)
  const last = stops[stops.length - 1];
  const returnLeg = legs.find(l => l.kind === 'return');
  if (returnLeg?.flight) {
    const f = returnLeg.flight;
    const src = f.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr';
    const srcLabel = src === 'chase_ur' ? 'Chase UR' : 'Amex MR';
    items.push({
      n: stepNum++,
      text: `Return · ${last.destination.city} → ${home}: Confirm ${tb.travelers} seat${tb.travelers === 1 ? '' : 's'} on ${f.airline_program} BEFORE transferring`,
      warning: true,
    });
    items.push({
      n: stepNum++,
      text: `Transfer ${tb.scaleForTravelers(f.total_points_two_travelers).toLocaleString()} ${srcLabel} → ${f.airline_program}, then book`,
      highlight: true,
      link: bookingLinks[src]?.transfer,
      linkLabel: `Open ${srcLabel} transfers`,
    });
  } else {
    items.push({
      n: stepNum++,
      text: `Return · ${last.destination.city} → ${home}: pick a flight on the Flights step (open the "Return" leg accordion in the per-leg award search)`,
      warning: true,
    });
  }

  return (
    <div id="booking-checklist" className="bg-navy-light rounded-xl border border-teal/30 p-5">
      <h3 className="font-bold text-teal mb-3">Multi-City Booking Checklist</h3>
      <div className="text-[11px] text-brand-ink/65 mb-3">
        {items.length} steps · {stops.length} stops · {tb.travelersLabel} · {Math.max(1, Math.round((new Date(tb.endDate) - new Date(tb.startDate)) / 86400000))} total nights
      </div>
      <div className="space-y-2.5 text-sm text-brand-ink/80">
        {items.map((it) => (
          <BookStep
            key={it.n}
            num={it.n}
            text={it.text}
            warning={it.warning}
            highlight={it.highlight}
            link={it.link}
            linkLabel={it.linkLabel}
          />
        ))}
      </div>
    </div>
  );
}

// Compute per-stop checkin/checkout date ranges from the trip's start date
// and each stop's night count. Returns one entry per stop (primary + extras).
function computeStopDateRanges(tb) {
  if (!tb.startDate) return [];
  const result = [];
  let cursor = new Date(tb.startDate + 'T12:00:00');
  for (const stop of tb.stops) {
    const checkin = cursor.toISOString().slice(0, 10);
    cursor = new Date(cursor.getTime() + (stop.nights || 1) * 86400000);
    const checkout = cursor.toISOString().slice(0, 10);
    result.push({ checkin, checkout, nights: stop.nights });
  }
  return result;
}

/* ── Multi-City Stops Picker ── */
// Compact per-stop duration hint. Shows recommended sweet spot + a
// 1-line basis citing sources (NYT 36 Hours, Rick Steves, Lonely Planet,
// r/travel consensus, etc.). Expandable for full day-by-day reasoning.
function StopDurationHint({ city, currentNights }) {
  const data = getCityDuration(city);
  if (!data) return null;
  const [low, high] = data.sweetSpot;
  const inRange = currentNights >= low && currentNights <= high;
  const tooShort = currentNights < data.minDays;
  const tooLong = currentNights > data.maxDays;
  const status = tooShort ? 'rushed' : tooLong ? 'padded' : inRange ? 'optimal' : 'okay';
  const statusCls = status === 'optimal' ? 'text-teal'
    : status === 'rushed' ? 'text-rose-400'
    : status === 'padded' ? 'text-amber-400'
    : 'text-brand-ink/65';
  const statusLabel = status === 'optimal' ? '✓ sweet spot'
    : status === 'rushed' ? '⚠ too rushed'
    : status === 'padded' ? '○ over-budget'
    : '○ okay';
  // Build a 1-line summary from the reasoning day count + the "why not more" hint
  const dayCount = Object.keys(data.reasoning || {}).filter(k => k !== 'whyNotMore').length;
  const summary = `${dayCount}-day blueprint covers the must-sees without padding.`;
  return (
    <details className="mt-2 group">
      <summary className="cursor-pointer text-[11px] text-brand-ink/70 flex items-center gap-2 list-none hover:text-brand-ink/90">
        <span className={`font-semibold ${statusCls}`}>
          {statusLabel}
        </span>
        <span>— recommended {low === high ? `${low}n` : `${low}-${high}n`} (optimal {data.optimal}n)</span>
        <span className="ml-auto text-[10px] text-brand-ink/55 group-open:hidden">Why · tap to expand</span>
        <span className="ml-auto text-[10px] text-brand-ink/55 hidden group-open:inline">Collapse</span>
      </summary>
      <div className="mt-2 space-y-2 text-[11px] text-brand-ink/80 leading-snug bg-brand-ink/5 rounded-md p-2">
        <div><strong className="text-brand-ink/95">Why this many:</strong> {summary}</div>
        {data.reasoning && Object.entries(data.reasoning).filter(([k]) => k !== 'whyNotMore').map(([k, v]) => (
          <div key={k} className="flex gap-1.5">
            <span className="text-[10px] font-bold text-teal uppercase shrink-0 w-10 pt-0.5">{k.replace('day', 'D')}</span>
            <span>{v}</span>
          </div>
        ))}
        {data.reasoning?.whyNotMore && (
          <div className="bg-amber-500/8 border border-amber-500/25 rounded p-1.5 text-[10px]">
            <strong className="text-amber-300">Why not more:</strong> {data.reasoning.whyNotMore}
          </div>
        )}
        {data.sources && data.sources.length > 0 && (
          <div className="pt-1 mt-1 border-t border-brand-ink/10 text-[10px] text-brand-ink/65">
            <strong className="text-brand-ink/80">Basis:</strong> {data.sources.join(' · ')}
          </div>
        )}
      </div>
    </details>
  );
}

function MultiCityStopsPicker({ primary, destinations, tb }) {
  const used = new Set([primary.id, ...tb.additionalStops.map(s => s.destinationId)]);
  const available = destinations.filter(d => !used.has(d.id));
  const totalNights = Math.max(1, Math.round((new Date(tb.endDate) - new Date(tb.startDate)) / 86400000));
  const additionalNights = tb.additionalStops.reduce((s, x) => s + (x.nights || 0), 0);
  const primaryNights = Math.max(1, totalNights - additionalNights);

  return (
    <div className="bg-gradient-to-r from-gold/8 to-teal/5 border border-gold/25 rounded-xl p-3 sm:p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-bold text-sm text-brand-ink">Your stops</div>
        <div className="text-[11px] text-brand-ink/65">{totalNights} total night{totalNights === 1 ? '' : 's'}</div>
      </div>

      {/* Primary stop — derived nights. The primary "stop 1" for a
          bundled destination (city = "Lisbon + Porto") is the FIRST city
          in the bundle (Lisbon), not the bundle itself. */}
      {(() => {
        const primaryParts = (primary.city || '').split(' + ').map(s => s.trim());
        const primaryFirstCity = primaryParts[0] || primary.city;
        return (
          <div className="bg-navy-light rounded-lg border border-teal/30 p-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-teal uppercase tracking-wider shrink-0">Stop 1</span>
              <span className="font-bold text-brand-ink truncate">{primaryFirstCity}</span>
              <span className="ml-auto text-xs font-medium text-brand-ink/75 shrink-0">
                {primaryNights} night{primaryNights === 1 ? '' : 's'}
                {additionalNights > 0 && (
                  <span className="text-[10px] text-brand-ink/55 ml-1">(remaining)</span>
                )}
              </span>
            </div>
            <StopDurationHint city={primaryFirstCity} currentNights={primaryNights} />
          </div>
        );
      })()}

      {/* Additional stops */}
      {tb.additionalStops.map((stop, i) => (
        <div key={i} className="bg-navy-light rounded-lg border border-gold/30 p-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-gold uppercase tracking-wider shrink-0">Stop {i + 2}</span>
            <span className="font-bold text-brand-ink truncate">{stop.destination.city}</span>
            <div className="ml-auto flex items-center gap-1.5 shrink-0">
              <input
                type="number"
                min={1}
                max={30}
                value={stop.nights}
                onChange={(e) => tb.updateStop(i, { nights: Math.max(1, Number(e.target.value) || 1) })}
                className="w-12 px-1.5 py-1 bg-brand-ink/10 border border-brand-ink/15 rounded text-xs text-right text-brand-ink"
              />
              <span className="text-[11px] text-brand-ink/65">night{stop.nights === 1 ? '' : 's'}</span>
              <button
                onClick={() => tb.removeStop(i)}
                className="ml-1 text-[10px] text-brand-ink/55 hover:text-brand-ink/85 px-1"
                aria-label="Remove stop"
              >
                ✕
              </button>
            </div>
          </div>
          <StopDurationHint city={stop.destination.city} currentNights={stop.nights} />
        </div>
      ))}

      {/* Add more */}
      {available.length > 0 ? (
        <details className="bg-brand-ink/5 rounded-lg border border-brand-ink/10 group">
          <summary className="cursor-pointer text-xs font-bold text-brand-ink/75 p-2.5 list-none flex items-center gap-2">
            <span className="text-base">+</span>
            <span>Add another city ({available.length} more available)</span>
          </summary>
          <div className="grid grid-cols-2 gap-1.5 p-2.5 pt-1 max-h-56 overflow-y-auto">
            {available.map(d => (
              <button
                key={d.id}
                onClick={() => tb.addStop(d, 3)}
                className="text-left px-2.5 py-1.5 bg-navy-light border border-brand-ink/15 rounded text-xs font-medium text-brand-ink hover:border-teal/50 hover:bg-teal/5 transition"
              >
                {d.city}
                <span className="ml-1 text-brand-ink/55 text-[10px]">{d.country}</span>
              </button>
            ))}
          </div>
          <CustomCityInput onAdd={(city, country, airport) => tb.addStop({
            id: `custom-${Date.now()}`,
            city, country: country || '',
            airport_codes: airport ? [airport] : [],
            _isCustom: true,
          }, 3)} />
        </details>
      ) : (
        <div className="bg-brand-ink/5 rounded-lg border border-brand-ink/10 p-2.5">
          <div className="text-[11px] text-brand-ink/55 italic mb-2">All catalog destinations added — or add your own:</div>
          <CustomCityInput onAdd={(city, country, airport) => tb.addStop({
            id: `custom-${Date.now()}`,
            city, country: country || '',
            airport_codes: airport ? [airport] : [],
            _isCustom: true,
          }, 3)} />
        </div>
      )}

      {tb.additionalStops.length === 0 && (
        <div className="text-[11px] text-brand-ink/65 bg-brand-ink/5 rounded-lg px-2.5 py-1.5">
          Pick at least one more city above to make this a multi-city trip — or switch back to Round Trip on the dates step.
        </div>
      )}
    </div>
  );
}

// CustomCityInput — add ANY city to the plan even if it's not in the
// catalog (e.g., The Algarve, Mykonos, Mendoza). Marked _isCustom on
// the destination object so Pilot knows to use web search instead of
// catalog when planning. For manual mode, the destination has no
// hotels/activities/restaurants prepopulated — user can chat with
// Pilot to populate.
// CustomPrimaryDestinationCard — lets user start a trip to ANY city
// not in the catalog. Generates a virtual destination with _isCustom
// flag + airport code. Backend Pilot uses web_search for everything
// (hotels, restaurants, activities, events). Live seats.aero still
// fires for flights using the airport code.
function CustomPrimaryDestinationCard({ onPick }) {
  const [expanded, setExpanded] = useState(false);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [airport, setAirport] = useState('');

  const submit = () => {
    if (!city.trim() || !airport.trim()) return;
    const dest = {
      id: `custom-primary-${Date.now()}`,
      city: city.trim(),
      country: country.trim() || '',
      airport_codes: [airport.trim().toUpperCase()],
      vibe_tags: [],
      description: `Custom destination — Pilot will source everything via web search.`,
      image_url: null,
      _isCustom: true,
    };
    onPick(dest);
    setExpanded(false);
    setCity(''); setCountry(''); setAirport('');
  };

  return (
    <div className="bg-gradient-to-br from-gold/10 to-teal/10 border-2 border-gold/40 rounded-2xl p-4 mb-4">
      {!expanded ? (
        <button onClick={() => setExpanded(true)} className="w-full text-left">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold to-teal flex items-center justify-center text-navy font-black text-2xl shrink-0">✨</div>
            <div className="flex-1">
              <div className="text-[10px] font-bold text-gold uppercase tracking-wider">CUSTOM DESTINATION</div>
              <div className="font-black text-base text-brand-ink mt-0.5">Plan to anywhere — Santorini, Mendoza, anywhere</div>
              <div className="text-[11px] text-brand-ink/70 mt-1 leading-snug">
                Don't see your destination? Type your own. Pilot researches hotels + activities + events live via web search.
                Flights pull from live seats.aero using the airport you provide.
              </div>
            </div>
            <div className="text-gold text-2xl">→</div>
          </div>
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-[10px] font-bold text-gold uppercase tracking-wider">Custom destination · all picks sourced via web</div>
          <input type="text" placeholder="City (e.g. Santorini, Mendoza, Cape Town)"
            value={city} onChange={(e) => setCity(e.target.value)} autoFocus
            className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-3 py-2 text-sm text-brand-ink" />
          <input type="text" placeholder="Country"
            value={country} onChange={(e) => setCountry(e.target.value)}
            className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-3 py-2 text-sm text-brand-ink" />
          <input type="text" placeholder="Nearest airport code (e.g. JTR, MDZ, CPT) — required for live flight search" maxLength={3}
            value={airport} onChange={(e) => setAirport(e.target.value.toUpperCase())}
            className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-3 py-2 text-sm text-brand-ink uppercase" />
          <div className="text-[10px] text-brand-ink/65 italic">
            Pilot will use web_search to find: hotels + restaurants + activities + current events for {city || 'this city'}. Live seats.aero will search awards from your home airport(s) to {airport || 'the airport'}.
          </div>
          <div className="flex gap-2">
            <button onClick={() => setExpanded(false)} className="px-4 py-2 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg">Cancel</button>
            <button onClick={submit} disabled={!city.trim() || !airport.trim()} className="flex-1 bg-gradient-to-r from-gold to-teal text-navy font-bold py-2 rounded-lg text-sm disabled:opacity-40">
              ✨ Use {city || 'this destination'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// MetroAirportPicker — quick checkbox to expand a single home airport
// to ALL nearby airports in the same metro area. Picking JFK auto-
// suggests "+ All NYC airports (EWR + LGA)" so the user can search the
// whole region without having to type each code.
function MetroAirportPicker({ primary, selected, onChange }) {
  const METRO_GROUPS = {
    NYC: ['JFK', 'EWR', 'LGA'],
    LA: ['LAX', 'BUR', 'LGB', 'SNA', 'ONT'],
    SF: ['SFO', 'OAK', 'SJC'],
    CHI: ['ORD', 'MDW'],
    DC: ['IAD', 'DCA', 'BWI'],
    HOU: ['IAH', 'HOU'],
    DAL: ['DFW', 'DAL'],
    MIA: ['MIA', 'FLL', 'PBI'],
    BOS: ['BOS', 'PVD', 'MHT'],
  };
  const metro = Object.entries(METRO_GROUPS).find(([, codes]) => codes.includes(primary));
  if (!metro) return null;
  const [metroName, codes] = metro;
  const allSelected = codes.every(c => selected.includes(c));
  return (
    <div className="bg-teal/5 border border-teal/30 rounded-lg p-2.5 mb-2 flex items-center justify-between gap-2">
      <div className="flex-1 min-w-0">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => onChange(e.target.checked ? codes : [primary])}
          />
          <span className="text-xs font-bold text-brand-ink">Search all {metroName} airports ({codes.join(' + ')})</span>
        </label>
        <div className="text-[10px] text-brand-ink/65 mt-0.5">
          {allSelected
            ? `Searching ${codes.length} airports — picking cheapest award across all of them`
            : `Currently only ${primary}. Often saves 20-30K miles when you check nearby airports too.`}
        </div>
      </div>
    </div>
  );
}

function CustomCityInput({ onAdd }) {
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [airport, setAirport] = useState('');
  const [expanded, setExpanded] = useState(false);
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full mt-2 text-xs font-bold bg-gradient-to-r from-teal/15 to-gold/15 border border-teal/40 text-brand-ink rounded p-2 hover:from-teal/25 hover:to-gold/25 transition"
      >
        ✨ Add custom city not in catalog (Pilot AI handles picks)
      </button>
    );
  }
  return (
    <div className="mt-2 bg-teal/5 border border-teal/30 rounded p-2 space-y-1.5">
      <div className="text-[10px] font-bold text-teal uppercase">Custom city · Pilot AI sources hotels + activities + events + transport</div>
      <input
        type="text" placeholder="City (e.g. The Algarve, Mendoza, Sayulita)"
        value={city} onChange={(e) => setCity(e.target.value)} autoFocus
        className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded px-2 py-1.5 text-xs text-brand-ink"
      />
      <input
        type="text" placeholder="Country (optional)"
        value={country} onChange={(e) => setCountry(e.target.value)}
        className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded px-2 py-1.5 text-xs text-brand-ink"
      />
      <input
        type="text" placeholder="Nearest airport code (e.g. FAO for Algarve) — Pilot will research if blank"
        value={airport} maxLength={3} onChange={(e) => setAirport(e.target.value.toUpperCase())}
        className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded px-2 py-1.5 text-xs text-brand-ink uppercase"
      />
      <div className="text-[10px] text-brand-ink/60 italic">Pilot will: find airport · search awards · suggest hotels/activities/events · plan transport from your previous stop</div>
      <div className="flex gap-1">
        <button onClick={() => setExpanded(false)} className="text-[10px] px-2 py-1 bg-brand-ink/10 text-brand-ink/85 rounded">Cancel</button>
        <button
          onClick={() => {
            if (city.trim()) {
              onAdd(city.trim(), country.trim(), airport.trim());
              setCity(''); setCountry(''); setAirport(''); setExpanded(false);
            }
          }}
          disabled={!city.trim()}
          className="flex-1 text-[10px] font-bold bg-teal text-navy rounded py-1 disabled:opacity-40"
        >
          + Add {city || 'city'}
        </button>
      </div>
    </div>
  );
}

/* ── Plan Detail Modal ── */
function PointsConversionGuide({ sourceProgram, targetProgram, partnerType = 'airline' }) {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetProgram) { setLoading(false); return; }
    setLoading(true);
    api.getTransferPartners(sourceProgram)
      .then(partners => {
        const match = (partners || []).find(p =>
          p.airline_program === targetProgram ||
          p.airline_program_short === targetProgram ||
          p.airline_program?.toLowerCase().includes(targetProgram.toLowerCase()) ||
          targetProgram.toLowerCase().includes(p.airline_program_short?.toLowerCase() || '')
        );
        setPartner(match || null);
      })
      .catch(() => setPartner(null))
      .finally(() => setLoading(false));
  }, [sourceProgram, targetProgram]);

  const sourceLink = bookingLinks[sourceProgram];
  const targetLink = bookingLinks[targetProgram];
  const SOURCE_NAMES = {
    amex_mr: 'Amex Membership Rewards',
    chase_ur: 'Chase Ultimate Rewards',
    capital_one: 'Capital One Venture',
    citi_ty: 'Citi ThankYou',
    bilt: 'Bilt Rewards',
  };
  const sourceName = SOURCE_NAMES[sourceProgram] || sourceProgram;

  // Cash-portal hotels — no transfer needed
  if (targetProgram === 'Chase Travel Portal' || targetProgram === 'Amex Travel' || targetProgram === 'Capital One Travel') {
    return (
      <div className="bg-teal/5 border border-teal/20 rounded-lg p-3 space-y-2">
        <div className="text-[10px] font-bold text-teal uppercase tracking-wider">How to book with points</div>
        <div className="text-sm text-brand-ink/80">
          No transfer needed — book directly through <span className="font-semibold">{targetProgram}</span> using your points at the portal redemption rate.
        </div>
        {targetLink && (
          <a href={targetLink.search} target="_blank" rel="noopener noreferrer"
            className="inline-block text-xs font-semibold text-teal hover:underline">
            Open {targetProgram} ↗
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-teal/5 to-indigo-500/5 border border-teal/20 rounded-lg p-3 space-y-2">
      <div className="text-[10px] font-bold text-teal uppercase tracking-wider">How to convert your points</div>
      {loading ? (
        <div className="text-xs text-brand-ink/55">Looking up transfer details…</div>
      ) : (
        <>
          <div className="text-sm text-brand-ink/85 font-medium">
            {sourceName} → {targetProgram}
            {partner && (
              <span className="text-brand-ink/65 font-normal"> · {Number(partner.transfer_ratio) === 1 ? '1:1' : `${partner.transfer_ratio}:1`} · {partner.transfer_time_description || (partner.is_instant ? 'Instant' : 'Allow 1-3 days')}</span>
            )}
          </div>
          {partner?.notes && <div className="text-[11px] text-brand-ink/65">{partner.notes}</div>}
          <ol className="text-xs text-brand-ink/75 space-y-1 list-decimal list-inside">
            <li>
              <strong>Verify award seats first</strong> on {targetProgram}{targetLink && (
                <> — <a href={targetLink.search} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">search here ↗</a></>
              )}. Do NOT transfer before confirming availability.
            </li>
            <li>
              <strong>Open {sourceName} transfer portal</strong>{sourceLink?.transfer && (
                <> — <a href={sourceLink.transfer} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">go to transfer page ↗</a></>
              )}.
            </li>
            <li>
              Select <strong>{targetProgram}</strong> as the partner and enter the exact number of points needed.
              {partner && !partner.is_instant && <> Transfer is <span className="text-gold">{partner.transfer_time_description}</span> — wait until points post.</>}
              {partner && partner.is_instant && <> Transfer is <span className="text-teal">instant</span>.</>}
            </li>
            <li>
              <strong>Book immediately</strong> on {targetProgram} once points arrive. Award seats can disappear quickly.
            </li>
          </ol>
          <div className="text-[10px] text-brand-ink/50 italic">Transfers are one-way and cannot be reversed.</div>
        </>
      )}
    </div>
  );
}

function PlanDetailModal({ item, type, onClose }) {
  const tb = useTripBuilder();
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-navy-light border border-brand-ink/10 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          className="absolute top-3 right-3 text-brand-cream hover:text-white-light text-2xl z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition shadow-lg">
          &times;
        </button>

        {type === 'flight' && (
          <div className="p-5 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${item.cabin_class === 'first' ? 'bg-gold/20 text-gold' : 'bg-indigo-500/20 text-indigo-300'}`}>
                  {item.cabin_class} class
                </span>
                {item.is_nonstop && <span className="px-2 py-0.5 bg-teal/20 rounded text-xs font-bold text-teal">NONSTOP</span>}
                {item.requires_round_trip && <span className="px-2 py-0.5 bg-brand-ink/10 rounded text-xs text-brand-ink/70">Round-trip required</span>}
              </div>
              <h3 className="text-lg font-bold">{item.routing_description}</h3>
              <div className="text-sm text-brand-ink/70 mt-1">via {item.airline_program} &middot; {item.aircraft_type}</div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">{tb.scaleForTravelers(item.total_points_two_travelers).toLocaleString()}</div>
                <div className="text-[10px] text-brand-ink/65 uppercase">Points ({tb.travelersLabel})</div>
              </div>
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">${tb.scaleForTravelers(item.total_estimated_taxes)}</div>
                <div className="text-[10px] text-brand-ink/65 uppercase">Taxes</div>
              </div>
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold"><ValueBadge cpp={parseFloat(item.cents_per_point)} size="sm" /></div>
                <div className="text-[10px] text-brand-ink/65 uppercase">Value</div>
              </div>
            </div>

            {item.cash_price_per_person && (
              <div className="bg-teal/10 border border-teal/20 rounded-lg p-3">
                <div className="text-xs font-bold text-teal uppercase mb-1">Points vs Cash</div>
                <div className="flex justify-between text-sm">
                  <span className="text-brand-ink/70">Cash price ({tb.travelersLabel}):</span>
                  <span className="text-brand-ink/80 line-through">${(item.cash_price_per_person * tb.travelers).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-brand-ink/70">With points:</span>
                  <span className="text-teal font-medium">{tb.scaleForTravelers(item.total_points_two_travelers).toLocaleString()} + ${tb.scaleForTravelers(item.total_estimated_taxes)}</span>
                </div>
                <div className="text-xs text-teal font-medium mt-1">Save ${((item.cash_price_per_person * tb.travelers) - tb.scaleForTravelers(item.total_estimated_taxes)).toLocaleString()}</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1">Flight</div>
                <div><span className="text-brand-ink/65">Duration:</span> {item.flight_duration_outbound}</div>
                <div><span className="text-brand-ink/65">Stops:</span> {item.is_nonstop ? 'Nonstop' : `${item.num_stops}`}</div>
                <div><span className="text-brand-ink/65">Transfer:</span> <span className={item.transfer_time === 'Instant' ? 'text-teal' : 'text-gold'}>{item.transfer_time}</span></div>
              </div>
              <div>
                <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1">Seat</div>
                <div className="font-medium">{item.seat_product}</div>
                {item.cabin_layout && <div className="text-brand-ink/70">Layout: {item.cabin_layout}</div>}
              </div>
            </div>

            {item.seat_description && (
              <div className="text-sm text-brand-ink/75">{item.seat_description}</div>
            )}

            {item.cabin_amenities && item.cabin_amenities.length > 0 && (
              <div>
                <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1.5">Cabin Amenities</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {item.cabin_amenities.map((a, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-sm text-brand-ink/75">
                      <span className="w-1 h-1 rounded-full bg-teal/50 flex-shrink-0" />{a}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-teal font-bold mb-1">PROS</div>
                {item.pros?.map((p, j) => <div key={j} className="text-xs text-brand-ink/75 mb-0.5">+ {p}</div>)}
              </div>
              <div>
                <div className="text-[10px] text-danger font-bold mb-1">CONS</div>
                {item.cons?.map((c, j) => <div key={j} className="text-xs text-brand-ink/75 mb-0.5">- {c}</div>)}
              </div>
            </div>

            <PointsConversionGuide
              sourceProgram={item.source_program || 'amex_mr'}
              targetProgram={item.airline_program}
              partnerType="airline"
            />

            {item.booking_steps && (
              <div>
                <div className="text-[10px] font-bold text-brand-ink/65 uppercase tracking-wider mb-1">Additional booking notes</div>
                <div className="bg-brand-ink/5 rounded-lg p-3 text-sm text-brand-ink/75 whitespace-pre-line">{item.booking_steps}</div>
              </div>
            )}
          </div>
        )}

        {type === 'hotel' && (
          <div className="p-5 space-y-4">
            <div>
              {item.star_rating && <div className="text-gold text-sm mb-1">{'★'.repeat(Math.floor(parseFloat(item.star_rating)))} <span className="text-brand-ink/55 text-xs">{item.review_count > 0 ? `(${item.review_count?.toLocaleString()} reviews)` : ''}</span></div>}
              <h3 className="text-lg font-bold">{item.hotel_name}</h3>
              <div className="text-sm text-brand-ink/70 mt-1">
                {item.neighborhood && <>{item.neighborhood} &middot; </>}via {item.hotel_program}
              </div>
            </div>

            <p className="text-sm text-brand-ink/75">{item.description}</p>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">{item.points_per_night?.toLocaleString()}</div>
                <div className="text-[10px] text-brand-ink/65 uppercase">Pts/Night</div>
              </div>
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">{item.nights}</div>
                <div className="text-[10px] text-brand-ink/65 uppercase">Nights</div>
              </div>
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">{item.total_points?.toLocaleString()}</div>
                <div className="text-[10px] text-brand-ink/65 uppercase">Total</div>
              </div>
            </div>

            {item.cash_price_per_night > 0 && (
              <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
                <div className="text-xs font-bold text-indigo-300 uppercase mb-1">Points vs Cash</div>
                <div className="flex justify-between text-sm">
                  <span className="text-brand-ink/70">Cash ({item.nights} nights):</span>
                  <span className="text-brand-ink/80 line-through">${(item.cash_price_per_night * item.nights).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-brand-ink/70">With points:</span>
                  <span className="text-indigo-300 font-medium">{item.total_points?.toLocaleString()} pts</span>
                </div>
                <div className="text-xs text-indigo-300 font-medium mt-1">
                  {((item.cash_price_per_night * item.nights / item.total_points) * 100).toFixed(1)} cpp value
                </div>
              </div>
            )}

            {item.amenities && item.amenities.length > 0 && (
              <div>
                <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1.5">Amenities</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                  {item.amenities.map((a, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-sm text-brand-ink/75 capitalize">
                      <span className="w-1 h-1 rounded-full bg-indigo-400/50 flex-shrink-0" />{a}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-teal font-bold mb-1">PROS</div>
                {item.pros?.map((p, j) => <div key={j} className="text-xs text-brand-ink/75 mb-0.5">+ {p}</div>)}
              </div>
              <div>
                <div className="text-[10px] text-danger font-bold mb-1">CONS</div>
                {item.cons?.map((c, j) => <div key={j} className="text-xs text-brand-ink/75 mb-0.5">- {c}</div>)}
              </div>
            </div>

            <PointsConversionGuide
              sourceProgram={item.source_program || (item.hotel_program?.includes('Hyatt') ? 'chase_ur' : 'amex_mr')}
              targetProgram={item.hotel_program}
              partnerType="hotel"
            />
          </div>
        )}

        {type === 'destination' && (
          <div className="p-5 space-y-4">
            {/* Hero */}
            <div className="h-40 -mx-5 -mt-5 relative overflow-hidden rounded-t-2xl">
              <DestImage city={item.city} />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-light via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-xl font-extrabold drop-shadow-lg">{item.city}</h3>
                <div className="text-sm text-brand-ink/85">{item.country} &middot; {item.region}</div>
              </div>
            </div>

            <VibeTags tags={item.vibe_tags} />
            <p className="text-sm text-brand-ink/80 leading-relaxed">{item.description}</p>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">{item.weather_high_f}°F</div>
                <div className="text-[10px] text-brand-ink/65">Sep Weather</div>
              </div>
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">${item.avg_daily_budget_min}-{item.avg_daily_budget_max}</div>
                <div className="text-[10px] text-brand-ink/65">Daily Budget</div>
              </div>
              <div className="bg-brand-ink/5 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold">{item.weather_rain_chance}</div>
                <div className="text-[10px] text-brand-ink/65">Rain Chance</div>
              </div>
            </div>

            {/* September Intel */}
            {septemberContext[item.city] && (() => {
              const ctx = septemberContext[item.city];
              return (
                <div className="bg-gradient-to-r from-teal/10 to-indigo-500/10 border border-teal/20 rounded-xl p-4 space-y-2">
                  <div className="text-xs font-bold text-teal uppercase">September Intel</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-brand-ink/65">Weather:</span> <span className="text-brand-ink/85">{ctx.weatherVibe}, {ctx.temp}</span></div>
                    <div><span className="text-brand-ink/65">Crowds:</span> <span className={`font-medium ${ctx.crowdLevel === 'Very Low' || ctx.crowdLevel === 'Low' ? 'text-teal' : 'text-gold'}`}>{ctx.crowdLevel}</span></div>
                  </div>
                  <div className="text-xs text-brand-ink/75">{ctx.crowdNote}</div>
                  {ctx.water && <div className="text-xs text-brand-ink/70">{ctx.water}</div>}
                  <div className="text-xs text-brand-ink/70">
                    <span className="text-brand-ink/65">In season:</span> {ctx.inSeason.join(' / ')}
                  </div>
                  {ctx.events && ctx.events.length > 0 && (
                    <div className="text-xs text-gold/80">
                      {ctx.events.map((e, j) => <div key={j}>* {e}</div>)}
                    </div>
                  )}
                  {ctx.warning && (
                    <div className="text-xs text-gold bg-gold/10 rounded-lg p-2">Heads up: {ctx.warning}</div>
                  )}
                  <div className="text-xs text-teal/70 italic">{ctx.bestFor}</div>
                </div>
              );
            })()}

            {/* Best flight */}
            {item.best_redemption?.[0] && (
              <div className="bg-brand-ink/5 rounded-xl p-3 border border-brand-ink/5 space-y-1">
                <div className="text-[10px] text-brand-ink/65 uppercase font-bold">Best Flight</div>
                <div className="text-sm font-medium">{item.best_redemption[0].routing_description}</div>
                <div className="flex items-center gap-2 text-xs text-brand-ink/70">
                  <span>{tb.scaleForTravelers(item.best_redemption[0].total_points_two_travelers).toLocaleString()} pts</span>
                  <span className="text-brand-ink/45">&middot;</span>
                  <span>{item.best_redemption[0].flight_duration_outbound}</span>
                  <span className="text-brand-ink/45">&middot;</span>
                  <span>{item.best_redemption[0].is_nonstop ? 'Nonstop' : `${item.best_redemption[0].num_stops} stop`}</span>
                  <ValueBadge cpp={parseFloat(item.best_redemption[0].cents_per_point)} size="sm" />
                </div>
              </div>
            )}

            {/* Best hotel */}
            {item.best_hotel?.[0] && (
              <div className="bg-brand-ink/5 rounded-xl p-3 border border-brand-ink/5 space-y-1">
                <div className="text-[10px] text-brand-ink/65 uppercase font-bold">Best Hotel</div>
                <div className="text-sm font-medium">{item.best_hotel[0].hotel_name}</div>
                <div className="text-xs text-brand-ink/70">{item.best_hotel[0].total_points?.toLocaleString()} pts &middot; {item.best_hotel[0].nights} nights</div>
              </div>
            )}

            {/* Best months */}
            {item.best_months && (
              <div className="text-xs text-brand-ink/65">Best months: <span className="text-brand-ink/75">{item.best_months.join(', ')}</span></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewCard({ title, complete, onEdit, children }) {
  return (
    <div className={`bg-navy-light rounded-xl border ${complete ? 'border-brand-ink/10' : 'border-dashed border-brand-ink/10'} p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${complete ? 'bg-teal text-navy' : 'bg-brand-ink/10 text-brand-ink/55'}`}>
            {complete ? '✓' : '?'}
          </div>
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <button onClick={onEdit} className="text-xs text-teal hover:underline">Edit</button>
      </div>
      <div className="ml-7">
        {children || <div className="text-xs text-brand-ink/45 italic">Not selected yet</div>}
      </div>
    </div>
  );
}

function reverseRoute(route) {
  if (!route) return '';
  // "JFK → Frankfurt → Dubrovnik on Lufthansa" → "Dubrovnik → Frankfurt → JFK on Lufthansa"
  const onMatch = route.match(/^(.+?)( on .+)?$/);
  const routePart = onMatch ? onMatch[1] : route;
  const airlinePart = onMatch && onMatch[2] ? onMatch[2] : '';
  const cities = routePart.split(/\s*→\s*/);
  return cities.reverse().join(' → ') + airlinePart;
}

function BookStep({ num, text, warning, highlight, link, linkLabel }) {
  return (
    <div className={`flex gap-3 ${highlight ? 'bg-teal/5 -mx-2 px-2 py-1 rounded' : ''}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${highlight ? 'bg-teal text-navy' : 'bg-brand-ink/10 text-brand-ink/65'}`}>{num}</div>
      <div className="flex-1">
        <div className={warning ? 'text-gold font-medium' : ''}>{text}</div>
        {warning && <div className="text-[10px] text-gold/60 mt-0.5">Transfers are irreversible!</div>}
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 px-3 py-1 bg-teal/10 text-teal text-[11px] font-medium rounded-lg hover:bg-teal/20 transition">
            {linkLabel || 'Open'} &rarr;
          </a>
        )}
      </div>
    </div>
  );
}

function SeptemberCard({ ctx, city }) {
  return (
    <div className="animate-fade-in-up bg-gradient-to-br from-navy-light to-navy-lighter rounded-2xl border border-teal/20 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold text-teal uppercase tracking-wider">September in {city}</div>
          <div className="text-sm text-brand-ink/80 mt-1">{ctx.bestFor}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-brand-ink">{ctx.temp}</div>
          <div className="text-[10px] text-brand-ink/65">{ctx.weatherVibe}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-brand-ink/5 rounded-lg p-3">
          <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1">Crowds</div>
          <div className={`text-sm font-semibold ${ctx.crowdLevel === 'Very Low' || ctx.crowdLevel === 'Low' ? 'text-teal' : ctx.crowdLevel === 'Low-Medium' ? 'text-teal/80' : 'text-gold'}`}>{ctx.crowdLevel}</div>
          <div className="text-[10px] text-brand-ink/65 mt-0.5">{ctx.crowdNote}</div>
        </div>
        <div className="bg-brand-ink/5 rounded-lg p-3">
          <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1">Water</div>
          <div className="text-sm text-brand-ink/80">{ctx.water}</div>
        </div>
      </div>

      <div>
        <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1.5">What's in season</div>
        <div className="flex flex-wrap gap-1.5">
          {ctx.inSeason.map((s, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 bg-teal/10 text-teal/80 rounded-full border border-teal/20">{s}</span>
          ))}
        </div>
      </div>

      {ctx.events.length > 0 && (
        <div>
          <div className="text-[10px] text-brand-ink/65 uppercase font-bold mb-1.5">Events &amp; festivals</div>
          {ctx.events.map((e, i) => (
            <div key={i} className="text-xs text-gold/80 flex items-center gap-1.5">
              <span className="text-gold">*</span> {e}
            </div>
          ))}
        </div>
      )}

      {ctx.warning && (
        <div className="bg-gold/10 border border-gold/20 rounded-lg p-2.5 text-xs text-gold/80">
          {ctx.warning}
        </div>
      )}
    </div>
  );
}

function CostSoFar({ tb, label }) {
  return (
    <div className="animate-fade-in bg-gradient-to-r from-navy-lighter to-navy-light rounded-xl border border-brand-ink/10 p-4">
      <div className="text-[10px] text-brand-ink/55 uppercase font-bold tracking-wider mb-2">Trip cost so far &middot; {label}</div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-lg font-black text-teal">{tb.totalPoints.toLocaleString()}</div>
          <div className="text-[10px] text-brand-ink/55">points used</div>
        </div>
        <div>
          <div className="text-lg font-black text-gold">${tb.totalCash.toLocaleString()}</div>
          <div className="text-[10px] text-brand-ink/55">cash costs</div>
        </div>
        <div>
          <div className={`text-lg font-black ${(tb.amexRemaining + tb.chaseRemaining) < 0 ? 'text-danger' : 'text-brand-ink/80'}`}>{(tb.amexRemaining + tb.chaseRemaining).toLocaleString()}</div>
          <div className="text-[10px] text-brand-ink/55">points remaining</div>
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <div className="flex-1 h-1.5 bg-brand-ink/10 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${Math.min((tb.flightPoints / tb.amexTotal) * 100, 100)}%` }} />
        </div>
        <div className="flex-1 h-1.5 bg-brand-ink/10 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-500 rounded-full transition-all duration-700" style={{ width: `${Math.min((tb.hotelPoints / tb.chaseTotal) * 100, 100)}%` }} />
        </div>
      </div>
      <div className="flex gap-2 mt-0.5">
        <div className="flex-1 text-[10px] text-blue-300/50">Amex MR: {tb.amexRemaining.toLocaleString()} left</div>
        <div className="flex-1 text-[10px] text-indigo-300/50">Chase UR: {tb.chaseRemaining.toLocaleString()} left</div>
      </div>
    </div>
  );
}

function cabinLabel(cabin) {
  return {
    economy: 'Economy',
    premium: 'Premium Economy',
    business: 'Business Class',
    first: 'First Class',
  }[cabin] || 'Business Class';
}

function FlightTimeBadge({ duration }) {
  // Parse "Xh Ym" format
  const match = duration?.match(/(\d+)h/);
  const hours = match ? parseInt(match[1]) : 0;
  const color = hours <= 6 ? 'bg-teal/15 text-teal' :
                hours <= 8 ? 'bg-gold/15 text-gold' :
                'bg-orange-500/15 text-orange-300';
  const note = hours > 8 ? ' (longer flight — worth it!)' : '';
  return (
    <span className={`${color} px-1.5 py-0.5 rounded-full text-[10px] font-bold`}>
      {duration}{note}
    </span>
  );
}

function SidebarItem({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-brand-ink/65 text-xs">{label}</span>
      {value ? (
        <div className="text-right">
          <div className="text-xs font-medium">{value}</div>
          {sub && <div className="text-[10px] text-brand-ink/55">{sub}</div>}
        </div>
      ) : (
        <span className="text-[10px] text-brand-ink/40">—</span>
      )}
    </div>
  );
}
